import React, { useEffect, useMemo, useRef, useState } from "react";
import { useCookies } from 'react-cookie';
import AdminSharedPresetPanel from "./AdminSharedPresetPanel";
import HoldAdminManualModal from "./HoldAdminManualModal";
import OpenPositionsSnapshotSection from "./OpenPositionsSnapshotSection";
import SymbolSearchPopup from "./SymbolSearchPopup";

const ADMIN_ACCESS_STORAGE_KEY = 'adminAccessContext';

function snapshotAnchorRect(el) {
  if (typeof el?.getBoundingClientRect !== "function") {
    return null;
  }
  const r = el.getBoundingClientRect();
  return {
    top: r.top,
    left: r.left,
    width: r.width,
    height: r.height,
    bottom: r.bottom,
    right: r.right,
  };
}

const getCookieValue = (name) => {
  if (typeof document === 'undefined') {
    return '';
  }
  const targetCookie = document.cookie
    .split('; ')
    .find((item) => item.startsWith(`${name}=`));
  if (!targetCookie) {
    return '';
  }
  return decodeURIComponent(targetCookie.split('=').slice(1).join('='));
};

const getInitialAdminAccessContext = (featureKey) => {
  try {
    const adminTargetUidCookie = getCookieValue('admin_target_uid');
    if (!adminTargetUidCookie) {
      return { from_admin: false };
    }
    const storedValue = sessionStorage.getItem(ADMIN_ACCESS_STORAGE_KEY);
    if (!storedValue) {
      return { from_admin: false };
    }
    const parsedValue = JSON.parse(storedValue);
    const fromAdmin =
      parsedValue?.from_admin === 1 ||
      parsedValue?.from_admin === '1' ||
      parsedValue?.from_admin === true;
    if (!fromAdmin || parsedValue?.feature !== featureKey) {
      return { from_admin: false };
    }
    return { from_admin: true };
  } catch {
    return { from_admin: false };
  }
};

export default function SetPage(){

    const [cookies] = useCookies(['uuid']);
    const [initialAdminAccess] = useState(() => getInitialAdminAccessContext('wz'));

    // 쿠키에서 UUID를 이용하여 사용자 정보 가져오기
    const[uid,setUID] = useState(0);
    const[nickname,setNickname] = useState('');
    const [isAdminAccess, setIsAdminAccess] = useState(() => initialAdminAccess.from_admin);
    const [adminManualTradeAllowed, setAdminManualTradeAllowed] = useState(false);
    const [adminManualModal, setAdminManualModal] = useState(null);
    const [snapshotReloadToken, setSnapshotReloadToken] = useState(0);
    const [memberHasSecondBitget, setMemberHasSecondBitget] = useState(false);

    const fetchWithCredentials = (url, options = {}) =>
      fetch(url, {
        credentials: 'include',
        ...options,
      });

    const [isChecked, setIsChecked] = useState(false);

    const [symbolList, setSymbolList] = useState([]);

    const [isPopupOpen, setIsPopupOpen] = useState(false);
    const [popupAnchor, setPopupAnchor] = useState(null);
    const [isSymbolMenuOpen, setIsSymbolMenuOpen] = useState(false);
    const symbolDropdownRef = useRef(null);

    const [candleText, setCandleText] = useState("최초 진입 기준 시간봉");

    const [leverageText, setLeverageText] = useState("롱진입 횟수");

    const [leverageText_short, setLeverageText_short] = useState("숏진입 횟수");

    const [benefit_text, setBenefitText] = useState("단일 익절률");

    const [setText_long, setSetText_long] = useState("롱진입 셋팅");

    const [setText_short, setSetText_short] = useState("숏진입 셋팅");

    useEffect(() => {
      const handleClickOutside = (event) => {
        if (symbolDropdownRef.current && !symbolDropdownRef.current.contains(event.target)) {
          setIsSymbolMenuOpen(false);
        }
      };

      const handleEscapeKey = (event) => {
        if (event.key === 'Escape') {
          setIsSymbolMenuOpen(false);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscapeKey);

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('keydown', handleEscapeKey);
      };
    }, []);

    useEffect(() => {
      if (!cookies.uuid) {
        redirectToLogin();
        return;
      }

      // uuid가 있을 때 서버로 요청 (authmiddleware가 처리)
      fetchUserInfo();
    }, [cookies.uuid]);

    const redirectToLogin = () => {
      alert('로그인 정보가 존재하지 않습니다. 로그인 페이지로 이동합니다.');
      window.location.href = isAdminAccess ? '/AdminLogin' : '/LoginAts';
    };
  
    const fetchUserInfo = () => {
      fetch(`/wzversion_selected`, { credentials: 'include' })
        .then(async (res) => {
          const data = await res.json().catch(() => ({}));
          if (!res.ok) {
            throw new Error(data.message || '페이지 접근 권한 확인에 실패했습니다.');
          }
          return data;
        })
        .then((data) => {
          const nextIsAdminAccess = Boolean(data?.is_admin_access);
          setIsAdminAccess(nextIsAdminAccess);
          setAdminManualTradeAllowed(Boolean(data?.admin_manual_trade_allowed));

          if (!nextIsAdminAccess) {
            sessionStorage.removeItem(ADMIN_ACCESS_STORAGE_KEY);
            document.cookie = 'admin_target_uid=; path=/; max-age=0; SameSite=Lax';
            setAdminManualTradeAllowed(false);
          }

          if (data.message !== 'ok') {
            alert(data.message);
            window.location.href = nextIsAdminAccess ? '/Adminpage' : '/SelectPage';
            return null;
          }
          return fetch('/GetUserInfo', { credentials: 'include' });
        })
        .then(async (res) => {
          if (!res) {
            return null;
          }
          const user = await res.json().catch(() => ({}));
          if (!res.ok) {
            throw new Error(user?.message || '사용자 정보 조회에 실패했습니다.');
          }
          return user;
        })
        .then((user) => {
          if (!user) {
            return;
          }
          setIsAdminAccess(Boolean(user?.is_admin_access));
          setMemberHasSecondBitget(
            Boolean(String(user?.uid_2 || '').trim()) && Boolean(String(user?.api_2 || '').trim())
          );
          setUID(user.uid);
          setNickname(user.nickname);
          fetchLists();
        })
        .catch((err) => {
          const message = err?.message || '로그인 정보가 존재하지 않습니다.';
          alert(message);
          window.location.href = isAdminAccess ? '/AdminLogin' : '/LoginAts';
        });
    };
    
    // selectedlevel, entry, waiting list 받아오기
    const fetchLists = () => {
      fetch(`/startcheck/searchlevel`)
        .then(response => {
          if (!response.ok) {
            throw new Error('서버 응답 실패');
          }
          return response.json();
        })
        .then(data => {
          if(data[0].super_user === "8"){
            setLevelCheck(data[0].super_user);  
          }else{
            setLevelCheck(data[0].selectedLevel);
          }
        })
        .catch(error => {
          console.error('Entry List 가져오기 오류:', error);
          alert(error);
        });

      fetch(`/startcheck_wz/list_entry`)
        .then(response => {
          if (!response.ok) {
            throw new Error('서버 응답 실패');
          }
          return response.json();
        })
        .then(data => {
          updateList(data, e_items, setE_Items);
        })
        .catch(error => {
          console.error('Entry List 가져오기 오류:', error);
          alert(error);
        });
  
      fetch(`/startcheck_wz/list_wait`)
        .then(response => {
          if (!response.ok) {
            throw new Error('서버 응답 실패');
          }
          return response.json();
        })
        .then(data => {
          updateList(data, w_items, setW_Items);
        })
        .catch(error => {
          console.error('Waiting List 가져오기 오류:', error);
          alert(error);
        });
    };
  
    const updateList = (data, list, setList) => {
      for (let i = 0; i < data.length; i++) {
        if (!isItemInList(data[i].symbol, list)) {
          setList(prevList => [...prevList, data[i].symbol]);
        }
      }
    };

    const isItemInList = (item, list) => {
      return list.includes(item);
    };

    const[seed,setSeed] = useState('basic');

    const[symbol,setSymbol] = useState('default');
    const selectableSymbols = useMemo(
      () => Array.from(new Set(symbolList.filter((sym) => sym && sym !== 'default'))),
      [symbolList]
    );
    const showAdminManualTrade = useMemo(
      () => Boolean(isAdminAccess && adminManualTradeAllowed),
      [isAdminAccess, adminManualTradeAllowed]
    );

    useEffect(() => {
      if (!showAdminManualTrade) {
        setAdminManualModal(null);
      }
    }, [showAdminManualTrade]);

    const currentSymbolLabel = symbol && symbol !== 'default' ? symbol : 'symbol select';

    const[entry_mode,setEntryMode] = useState('kinpoint');

    const[first_usdt,setFirstUsdt] = useState(10);
    const[leverage_info, setLeverage] = useState(20);

    const[sensitivity,setSensitivity] = useState(2);
    const[atr_period,setAtrPeriod] = useState(2);

    const[entry_standard,setEntryStandard] = useState('no');
    const[entry_signal,setEntrySignal] = useState('ut');

    const[entry_standard_type,setEntryStandardType] = useState('haikin');
    const[entry_standard_candle,setEntryStandardCandle] = useState('4h');

    const[ema_standard,setEmaStandard] = useState(200);
    const[ema_losscut,setEmaLosscut] = useState('no');
    const[ema_entry_gap_ratio,setEmaEntryGapRatio] = useState(0);
    
    const[sensitivity_standard,setSensitivityStandard] = useState(2);
    const[atr_period_standard,setAtrPeriodStandard] = useState(2);

    const[sensitivity_close,setSensitivityClose] = useState(1);
    const[atr_period_close,setAtrPeriodClose] = useState(1);

    const[profit,setProfit] = useState(1);
    const[losscut,setLosscut] = useState(1.5);

    const[mix_benefit,setMixbenefit] = useState(0.35);
    const[mix_benefit_reentry,setMixBenefitReentry] = useState('off');
    const[occurrence_losscut,setOccurrenceLosscut] = useState('off');

    const[entry_candle,setEntryCandle] = useState('5m');

    const[add_position,setAddposition] = useState(2);

    const[add_signals,setAddsignals] = useState('ut');

    const[entry_checking,setEntryChecking] = useState(-1);
    const[standard_checking,setStandardChecking] = useState(-1);
    const[benefit_checking,setBenefitChecking] = useState(-1);

    const[single_double_benefit,setSingleDoubleBenefit] = useState('double');

    const[benefit_standard,setBenefitStandard] = useState('kinpoint');

    //////////////////////////////////////////////////////////

    const[ma_1st,setMa1st] = useState(3);
    const[ma_2nd,setMa2nd] = useState(6);
    const[ma_3rd,setMa3rd] = useState(9);
    const[rsi_length,setRsiLength] = useState(7);
    const[low_rsi,setLowRsi] = useState(30);
    const[high_rsi,setHighRsi] = useState(70);
    const[mix_candle,setMixCandle] = useState('1h');
    const[close_candle,setCloseCandle] = useState('15m');

    const[direction,setDirection] = useState('both');

    //////////////////////////////////////////////////////////

    const[candle_type_entry, setCandleTypeEntry] = useState('haikin');
    const[candle_type_close, setCandleTypeClose] = useState('haikin');

    const[min_benefit_ratio,setMinBenefitRatio] = useState(0.3);
    const[both_benefit_ratio,setBothBenefitRatio] = useState(0.3);

    const[total_benefit_usdt,setTotalBenefitUsdt] = useState(0);
    const[total_benefit_usdt_obj,setTotalBenefitUsdtObj] = useState(0);

    const[long_count,setLongCount] = useState(0)
    const[short_count,setShortCount] = useState(0)

    const[entry_usdt_1, setEntryUsdt_1] = useState(10);
    const[entry_usdt_2, setEntryUsdt_2] = useState(10);
    const[entry_usdt_3, setEntryUsdt_3] = useState(10);
    const[entry_usdt_4, setEntryUsdt_4] = useState(0);
    const[entry_usdt_5, setEntryUsdt_5] = useState(0);
    const[entry_usdt_6, setEntryUsdt_6] = useState(0);
    const[entry_usdt_7, setEntryUsdt_7] = useState(0);
    const[entry_usdt_8, setEntryUsdt_8] = useState(0);
    const[entry_usdt_9, setEntryUsdt_9] = useState(0);

    const[entry_ratio_1, setEntryRatio_1] = useState(3);
    const[entry_ratio_2, setEntryRatio_2] = useState(3);
    const[entry_ratio_3, setEntryRatio_3] = useState(3);
    const[entry_ratio_4, setEntryRatio_4] = useState(0);
    const[entry_ratio_5, setEntryRatio_5] = useState(0);
    const[entry_ratio_6, setEntryRatio_6] = useState(0);
    const[entry_ratio_7, setEntryRatio_7] = useState(0);
    const[entry_ratio_8, setEntryRatio_8] = useState(0);
    const[entry_ratio_9, setEntryRatio_9] = useState(0);

    const[entry_usdt_add_1, setEntryUsdtAdd_1] = useState(10);
    const[entry_usdt_add_2, setEntryUsdtAdd_2] = useState(10);
    const[entry_usdt_add_3, setEntryUsdtAdd_3] = useState(10);
    const[entry_usdt_add_4, setEntryUsdtAdd_4] = useState(0);
    const[entry_usdt_add_5, setEntryUsdtAdd_5] = useState(0);
    const[entry_usdt_add_6, setEntryUsdtAdd_6] = useState(0);
    const[entry_usdt_add_7, setEntryUsdtAdd_7] = useState(0);
    const[entry_usdt_add_8, setEntryUsdtAdd_8] = useState(0);
    const[entry_usdt_add_9, setEntryUsdtAdd_9] = useState(0);

    const[entry_ratio_add_1, setEntryRatioAdd_1] = useState(3);
    const[entry_ratio_add_2, setEntryRatioAdd_2] = useState(3);
    const[entry_ratio_add_3, setEntryRatioAdd_3] = useState(3);
    const[entry_ratio_add_4, setEntryRatioAdd_4] = useState(0);
    const[entry_ratio_add_5, setEntryRatioAdd_5] = useState(0);
    const[entry_ratio_add_6, setEntryRatioAdd_6] = useState(0);
    const[entry_ratio_add_7, setEntryRatioAdd_7] = useState(0);
    const[entry_ratio_add_8, setEntryRatioAdd_8] = useState(0);
    const[entry_ratio_add_9, setEntryRatioAdd_9] = useState(0);

    //////////////////////////////////////////////////////////
    
    const[firstusdtValid, setFirstusdtValid] = useState(true);
    const[leverageValid,setLeverageValid] = useState(true);

    const[sensitivityValid, setSensitivityValid] = useState(true);
    const[atrperiodValid, setAtrperiodValid] = useState(true);

    const[emaStandardValid, setEmaStandardValid] = useState(true);
  
    const[sensitivityStandardValid, setSensitivityStandardValid] = useState(true);
    const[atrperiodStandardValid, setAtrperiodStandardValid] = useState(true);

    const[sensitivityCloseValid, setSensitivityCloseValid] = useState(true);
    const[atrperiodCloseValid, setAtrperiodCloseValid] = useState(true);

    const[profitValid, setProfitValid] = useState(true);
    const[losscutValid, setLosscutValid] = useState(true);

    const[ma_1stValid,setMa1stValid] = useState(true);
    const[ma_2ndValid,setMa2ndValid] = useState(true);
    const[ma_3rdValid,setMa3rdValid] = useState(true);
    const[rsi_lengthValid,setRsiLengthValid] = useState(true);
    const[low_rsiValid,setLowRsiValid] = useState(true);
    const[high_rsitValid,setHighRsiValid] = useState(true);

    const[min_benefit_ratioratioValid, setMinBenefitRatioratioValid] = useState(true);
    const[both_benefit_ratioratioValid, setBothBenefitRatioratioValid] = useState(true);

    const[total_benefit_usdtValid, setTotalBenefitusdtValid] = useState(true);

    const[entryusdtValid_1,setEntryUsdtValid_1] = useState(true);
    const[entryusdtValid_2,setEntryUsdtValid_2] = useState(true);
    const[entryusdtValid_3,setEntryUsdtValid_3] = useState(true);
    const[entryusdtValid_4,setEntryUsdtValid_4] = useState(true);
    const[entryusdtValid_5,setEntryUsdtValid_5] = useState(true);
    const[entryusdtValid_6,setEntryUsdtValid_6] = useState(true);
    const[entryusdtValid_7,setEntryUsdtValid_7] = useState(true);
    const[entryusdtValid_8,setEntryUsdtValid_8] = useState(true);
    const[entryusdtValid_9,setEntryUsdtValid_9] = useState(true);

    const[entryratioValid_1,setEntryRatioValid_1] = useState(true);
    const[entryratioValid_2,setEntryRatioValid_2] = useState(true);
    const[entryratioValid_3,setEntryRatioValid_3] = useState(true);
    const[entryratioValid_4,setEntryRatioValid_4] = useState(true);
    const[entryratioValid_5,setEntryRatioValid_5] = useState(true);
    const[entryratioValid_6,setEntryRatioValid_6] = useState(true);
    const[entryratioValid_7,setEntryRatioValid_7] = useState(true);
    const[entryratioValid_8,setEntryRatioValid_8] = useState(true);
    const[entryratioValid_9,setEntryRatioValid_9] = useState(true);

    const[entryusdtAddValid_1,setEntryUsdtAddValid_1] = useState(true);
    const[entryusdtAddValid_2,setEntryUsdtAddValid_2] = useState(true);
    const[entryusdtAddValid_3,setEntryUsdtAddValid_3] = useState(true);
    const[entryusdtAddValid_4,setEntryUsdtAddValid_4] = useState(true);
    const[entryusdtAddValid_5,setEntryUsdtAddValid_5] = useState(true);
    const[entryusdtAddValid_6,setEntryUsdtAddValid_6] = useState(true);
    const[entryusdtAddValid_7,setEntryUsdtAddValid_7] = useState(true);
    const[entryusdtAddValid_8,setEntryUsdtAddValid_8] = useState(true);
    const[entryusdtAddValid_9,setEntryUsdtAddValid_9] = useState(true);

    const[entryratioAddValid_1,setEntryRatioAddValid_1] = useState(true);
    const[entryratioAddValid_2,setEntryRatioAddValid_2] = useState(true);
    const[entryratioAddValid_3,setEntryRatioAddValid_3] = useState(true);
    const[entryratioAddValid_4,setEntryRatioAddValid_4] = useState(true);
    const[entryratioAddValid_5,setEntryRatioAddValid_5] = useState(true);
    const[entryratioAddValid_6,setEntryRatioAddValid_6] = useState(true);
    const[entryratioAddValid_7,setEntryRatioAddValid_7] = useState(true);
    const[entryratioAddValid_8,setEntryRatioAddValid_8] = useState(true);
    const[entryratioAddValid_9,setEntryRatioAddValid_9] = useState(true);

    const[mixBenefitValid,setMixBenefitValid] = useState(true);

    const[setlevel, setLevelCheck] = useState(0);

    const[notAllow, setNotAllow] = useState(true);

    const[e_items, setE_Items] = useState([]);
    const[w_items, setW_Items] = useState([]);

    const handleCheckboxChange = (event) => {
      setIsChecked(event.target.checked);
    };

    const[search,setSearch] = useState(false);

    const[trade_start,setTradeStart] = useState(false);
    
    const[benefit_stop,setBenefit_stop] = useState(false);
    const[benefit_cancel,setBenefit_cancel] = useState(false);

    const[currentCoin_stop,setCurrentCoin_stop] = useState(false);
    const[allCoin_stop,setAllCoin_stop] = useState(false);

    const[end,setEnd] = useState(false);
    
    // 유효성 검사 //// 유효성 검사 //// 유효성 검사 //// 유효성 검사 //// 유효성 검사 //// 유효성 검사 //
    const handleEntryMode = (e) => {
      setEntryMode(e.target.value);
        };

    const handleEntryCandle = (e) => {
      setEntryCandle(e.target.value);
        };
      
    const handleAddPosition = (e) => {
      setAddposition(e.target.value);
        };

    const handleEntryStandard = (e) => {
      setEntryStandard(e.target.value);
        };

    const handleEntrySignal = (e) => {
      setEntrySignal(e.target.value);
        };
    
    const handleEmaLosscut = (e) => {
      setEmaLosscut(e.target.value);
        };

    const handleEmaEntryGapRatio = (e) => {
      const raw = e.target.value.trim();
      if (raw === '') {
        setEmaEntryGapRatio(0);
        return;
      }
      const num = parseFloat(raw);
      setEmaEntryGapRatio(Number.isFinite(num) && num >= 0 ? num : 0);
    };

    const handleEntryStandardType = (e) => {
      setEntryStandardType(e.target.value);
        };
    
    const handleEntryStandardCandle = (e) => {
      setEntryStandardCandle(e.target.value);
        };

    const handleSingleDoubleBenefit = (e) => {
      setSingleDoubleBenefit(e.target.value);
        };

    const handleAddSignals = (e) => {
      setAddsignals(e.target.value);
        };
    
    const handleEntryEntryChecking = (e) => {
      setEntryChecking(e.target.value);
        };

    const handleStandardChecking = (e) => {
      setStandardChecking(e.target.value);
        };
  
    const handleBenefitChecking = (e) => {
      setBenefitChecking(e.target.value);
        };
            
    const handleBenefitStandard = (e) => {
      setBenefitStandard(e.target.value);
        };

    const handleMixCandle = (e) => {
      setMixCandle(e.target.value);
        };

    const handleCloseCandle = (e) => {
      setCloseCandle(e.target.value);
        };
    
    const handleDirection = (e) => {
      setDirection(e.target.value);
        };
    
    const handleCandleTypeEntry = (e) => {
      setCandleTypeEntry(e.target.value);
        };
    
    const handleCandleTypeClose = (e) => {
      setCandleTypeClose(e.target.value);
        };

    const handleFirstUsdt = (e) => {
      const newFirstusdt = e.target.value.trim();
      setFirstUsdt(newFirstusdt);
        const regex = 
        /^\d*\.?\d*$/;
        if (regex.test(newFirstusdt)) {
          setFirstusdtValid(true);
          } 
        else {
          setFirstusdtValid(false);
          }
        };
    
    const handleLeverage = (e) => {
      const newLeverage = e.target.value.trim();
      setLeverage(newLeverage);
        const regex = 
        /^\d*$/;
        if (regex.test(newLeverage)) {
          setLeverageValid(true);
          } 
        else {
          setLeverageValid(false);
          }
        };
    
    const handleEmaStandard = (e) => {
      const newemastandard = e.target.value.trim();
      setEmaStandard(newemastandard);
        const regex = 
        /^\d*$/;
        if (regex.test(newemastandard)) {
          setEmaStandardValid(true);
          } 
        else {
          setEmaStandardValid(false);
          }
        };

    const handleSensitivity = (e) => {
      const newSensitivity = e.target.value.trim();
      setSensitivity(newSensitivity);
        const regex = 
        /^\d+$/;
        if (regex.test(newSensitivity)) {
          setSensitivityValid(true);
          } 
        else {
          setSensitivityValid(false);
          }
        };
    
    const handleAtrperiod = (e) => {
      const newdata = e.target.value.trim();
      setAtrPeriod(newdata);
        const regex = 
        /^\d+$/;
        if (regex.test(newdata)) {
          setAtrperiodValid(true);
          } 
        else {
          setAtrperiodValid(false);
          }
        };

    const handleSensitivityStandard = (e) => {
      const newSensitivityStandard = e.target.value.trim();
      setSensitivityStandard(newSensitivityStandard);
        const regex = 
        /^\d+$/;
        if (regex.test(newSensitivityStandard)) {
          setSensitivityStandardValid(true);
          } 
        else {
          setSensitivityStandardValid(false);
          }
        };
    
    const handleAtrperiodStandard = (e) => {
      const newdata = e.target.value.trim();
      setAtrPeriodStandard(newdata);
        const regex = 
        /^\d+$/;
        if (regex.test(newdata)) {
          setAtrperiodStandardValid(true);
          } 
        else {
          setAtrperiodStandardValid(false);
          }
        };


    const handleSensitivityClose = (e) => {
      const newSensitivity = e.target.value.trim();
      setSensitivityClose(newSensitivity);
        const regex = 
        /^\d+$/;
        if (regex.test(newSensitivity)) {
          setSensitivityCloseValid(true);
          } 
        else {
          setSensitivityCloseValid(false);
          }
        };
    
    const handleAtrperiodClose = (e) => {
      const newdata = e.target.value.trim();
      setAtrPeriodClose(newdata);
        const regex = 
        /^\d+$/;
        if (regex.test(newdata)) {
          setAtrperiodCloseValid(true);
          } 
        else {
          setAtrperiodCloseValid(false);
          }
        };
  
    const handleProfit = (e) => {
      const newProfit = e.target.value.trim();
      setProfit(newProfit);
        const regex = 
        /^\d+$/;
        if (regex.test(newProfit)) {
          setProfitValid(true);
          } 
        else {
          setProfitValid(false);
          }
        };
    
    const handleLosscut = (e) => {
      const newdata = e.target.value.trim();
      setLosscut(newdata);
        const regex = 
        /^\d+$/;
        if (regex.test(newdata)) {
          setLosscutValid(true);
          } 
        else {
          setLosscutValid(false);
          }
        };

    const handleMa1st = (e) => {
      const newMa_1st= e.target.value.trim();
      setMa1st(newMa_1st);
        const regex = 
        /^\d*\.?\d*$/;
        if (regex.test(newMa_1st)) {
          setMa1stValid(true);
          } 
        else {
          setMa1stValid(false);
          }
        };
    
    const handleMa2nd = (e) => {
      const newMa_2nd= e.target.value.trim();
      setMa2nd(newMa_2nd);
        const regex = 
        /^\d*\.?\d*$/;
        if (regex.test(newMa_2nd)) {
          setMa2ndValid(true);
          } 
        else {
          setMa2ndValid(false);
          }
        };

    const handleMa3rd = (e) => {
      const newMa_3rd= e.target.value.trim();
      setMa3rd(newMa_3rd);
        const regex = 
        /^\d*\.?\d*$/;
        if (regex.test(newMa_3rd)) {
          setMa3rdValid(true);
          } 
        else {
          setMa3rdValid(false);
          }
        };
    
    const handleRsiLength = (e) => {
      const newRsi_length = e.target.value.trim();
      setRsiLength(newRsi_length);
        const regex = 
        /^\d*\.?\d*$/;
        if (regex.test(newRsi_length)) {
          setRsiLengthValid(true);
          } 
        else {
          setRsiLengthValid(false);
          }
        };
    
    const handleLowRsi = (e) => {
      const newLow_rsi = e.target.value.trim();
      setLowRsi(newLow_rsi);
        const regex = 
        /^\d*\.?\d*$/;
        if (regex.test(newLow_rsi)) {
          setLowRsiValid(true);
          } 
        else {
          setLowRsiValid(false);
          }
        };

    const handleHighRsi = (e) => {
      const newHigh_rsi = e.target.value.trim();
      setHighRsi(newHigh_rsi);
        const regex = 
        /^\d*\.?\d*$/;
        if (regex.test(newHigh_rsi)) {
          setHighRsiValid(true);
          } 
        else {
          setHighRsiValid(false);
          }
        };

    const handleMinBenefitratio = (e) => {
      const newMinBenefitratio = e.target.value.trim();
      setMinBenefitRatio(newMinBenefitratio);
        const regex = 
        /^\d*\.?\d*$/;
        if (regex.test(newMinBenefitratio)) {
          setMinBenefitRatioratioValid(true);
          } 
        else {
          setMinBenefitRatioratioValid(false);
          }
        };
    
    const handleBothBenefitratio = (e) => {
      const newBothBenefitratio = e.target.value.trim();
      setBothBenefitRatio(newBothBenefitratio);
        const regex = 
        /^\d*\.?\d*$/;
        if (regex.test(newBothBenefitratio)) {
          setBothBenefitRatioratioValid(true);
          } 
        else {
          setBothBenefitRatioratioValid(false);
          }
        };
    
    const handleMixBenefit = (e) => {
      const newmixbenefit = e.target.value.trim();
      setMixbenefit(newmixbenefit);
        const regex = 
        /^\d*\.?\d*$/;
        if (regex.test(newmixbenefit)) {
          setMixBenefitValid(true);
          } 
        else {
          setMixBenefitValid(false);
          }
        };

    const handleMixBenefitReentry = (e) => {
      if (occurrence_losscut === 'apply') {
        return;
      }
      setMixBenefitReentry(e.target.value);
    };

    const handleOccurrenceLosscut = (e) => {
      const next = e.target.value;
      setOccurrenceLosscut(next);
      if (next === 'apply') {
        setMixBenefitReentry('off');
      }
    };

    const handleTotalBenefitusdt = (e) => {
      const newTotalBenefitusdt = e.target.value.trim();
      setTotalBenefitUsdt(newTotalBenefitusdt);
        const regex = 
        /^\d*\.?\d*$/;
        if (regex.test(newTotalBenefitusdt)) {
          setTotalBenefitusdtValid(true);
          } 
        else {
          setTotalBenefitusdtValid(false);
          }
        };
    
    
    //222222222 진입22222222 USDT 유효성 검사 //// 진입 USDT 유효성 검사 //// 진입 USDT 유효성 검사 //
    const handleEntryUsdtAdd_1 = (e) => {
      const newEntryUsdt = e.target.value.trim();
      setEntryUsdtAdd_1(newEntryUsdt);
        const regex = 
        /^\d*\.?\d*$/;
        if (regex.test(newEntryUsdt)) {
          setEntryUsdtAddValid_1(true);
          } 
        else {
          setEntryUsdtAddValid_1(false);
          }
        };
    const handleEntryUsdtAdd_2 = (e) => {
      const newEntryUsdt = e.target.value.trim();
      setEntryUsdtAdd_2(newEntryUsdt);
        const regex = 
        /^\d*\.?\d*$/;
        if (regex.test(newEntryUsdt)) {
          setEntryUsdtAddValid_2(true);
          } 
        else {
          setEntryUsdtAddValid_2(false);
          }
        };
    const handleEntryUsdtAdd_3 = (e) => {
      const newEntryUsdt = e.target.value.trim();
      setEntryUsdtAdd_3(newEntryUsdt);
        const regex = 
        /^\d*\.?\d*$/;
        if (regex.test(newEntryUsdt)) {
          setEntryUsdtAddValid_3(true);
          } 
        else {
          setEntryUsdtAddValid_3(false);
          }
        };
    const handleEntryUsdtAdd_4 = (e) => {
      const newEntryUsdt = e.target.value.trim();
      setEntryUsdtAdd_4(newEntryUsdt);
        const regex = 
        /^\d*\.?\d*$/;
        if (regex.test(newEntryUsdt)) {
          setEntryUsdtAddValid_4(true);
          } 
        else {
          setEntryUsdtAddValid_4(false);
          }
        };
    const handleEntryUsdtAdd_5 = (e) => {
      const newEntryUsdt = e.target.value.trim();
      setEntryUsdtAdd_5(newEntryUsdt);
        const regex = 
        /^\d*\.?\d*$/;
        if (regex.test(newEntryUsdt)) {
          setEntryUsdtAddValid_5(true);
          } 
        else {
          setEntryUsdtAddValid_5(false);
          }
        };
    const handleEntryUsdtAdd_6 = (e) => {
      const newEntryUsdt = e.target.value.trim();
      setEntryUsdtAdd_6(newEntryUsdt);
        const regex = 
        /^\d*\.?\d*$/;
        if (regex.test(newEntryUsdt)) {
          setEntryUsdtAddValid_6(true);
          } 
        else {
          setEntryUsdtAddValid_6(false);           
          }
        };
    const handleEntryUsdtAdd_7 = (e) => {
      const newEntryUsdt = e.target.value.trim();
      setEntryUsdtAdd_7(newEntryUsdt);
        const regex = 
        /^\d*\.?\d*$/;
        if (regex.test(newEntryUsdt)) {
          setEntryUsdtAddValid_7(true);
          } 
        else {
          setEntryUsdtAddValid_7(false);
          }
        };
    const handleEntryUsdtAdd_8 = (e) => {
      const newEntryUsdt = e.target.value.trim();
      setEntryUsdtAdd_8(newEntryUsdt);
        const regex = 
        /^\d*\.?\d*$/;
        if (regex.test(newEntryUsdt)) {
          setEntryUsdtAddValid_8(true);
          } 
        else {
          setEntryUsdtAddValid_8(false);
          }
        };
    const handleEntryUsdtAdd_9 = (e) => {
      const newEntryUsdt = e.target.value.trim();
      setEntryUsdtAdd_9(newEntryUsdt);
        const regex = 
        /^\d*\.?\d*$/;
        if (regex.test(newEntryUsdt)) {
          setEntryUsdtAddValid_9(true);
          } 
        else {
          setEntryUsdtAddValid_9(false);
          }
        };

        
    const handleEntryRatioAdd_1 = (e) => {
      const newEntryRatio = e.target.value.trim();
      setEntryRatioAdd_1(newEntryRatio);
        const regex = 
        /^\d*\.?\d*$/;
        if (regex.test(newEntryRatio)) {
          setEntryRatioAddValid_1(true);
          } 
        else {
          setEntryRatioAddValid_1(false);
          }
        };
    const handleEntryRatioAdd_2 = (e) => {
      const newEntryRatio = e.target.value.trim();
      setEntryRatioAdd_2(newEntryRatio);
        const regex = 
        /^\d*\.?\d*$/;
        if (regex.test(newEntryRatio)) {
          setEntryRatioAddValid_2(true);
          } 
        else {
          setEntryRatioAddValid_2(false);
          }
        };
    const handleEntryRatioAdd_3 = (e) => {
      const newEntryRatio = e.target.value.trim();
      setEntryRatioAdd_3(newEntryRatio);
        const regex = 
        /^\d*\.?\d*$/;
        if (regex.test(newEntryRatio)) {
          setEntryRatioAddValid_3(true);
          } 
        else {
          setEntryRatioAddValid_3(false);
          }
        };
    const handleEntryRatioAdd_4 = (e) => {
      const newEntryRatio = e.target.value.trim();
      setEntryRatioAdd_4(newEntryRatio);
        const regex = 
        /^\d*\.?\d*$/;
        if (regex.test(newEntryRatio)) {
          setEntryRatioAddValid_4(true);
          } 
        else {
          setEntryRatioAddValid_4(false);
          }
        };
    const handleEntryRatioAdd_5 = (e) => {
      const newEntryRatio = e.target.value.trim();
      setEntryRatioAdd_5(newEntryRatio);
        const regex = 
        /^\d*\.?\d*$/;
        if (regex.test(newEntryRatio)) {
          setEntryRatioAddValid_5(true);
          } 
        else {
          setEntryRatioAddValid_5(false);
          }
        };
    const handleEntryRatioAdd_6 = (e) => {
      const newEntryRatio = e.target.value.trim();
      setEntryRatioAdd_6(newEntryRatio);
        const regex = 
        /^\d*\.?\d*$/;
        if (regex.test(newEntryRatio)) {
          setEntryRatioAddValid_6(true);
          } 
        else {
          setEntryRatioAddValid_6(false);           
          }
        };
    const handleEntryRatioAdd_7 = (e) => {
      const newEntryRatio = e.target.value.trim();
      setEntryRatioAdd_7(newEntryRatio);
        const regex = 
        /^\d*\.?\d*$/;
        if (regex.test(newEntryRatio)) {
          setEntryRatioAddValid_7(true);
          } 
        else {
          setEntryRatioAddValid_7(false);
          }
        };
    const handleEntryRatioAdd_8 = (e) => {
      const newEntryRatio = e.target.value.trim();
      setEntryRatioAdd_8(newEntryRatio);
        const regex = 
        /^\d*\.?\d*$/;
        if (regex.test(newEntryRatio)) {
          setEntryRatioAddValid_8(true);
          } 
        else {
          setEntryRatioAddValid_8(false);
          }
        };
    const handleEntryRatioAdd_9 = (e) => {
      const newEntryRatio = e.target.value.trim();
      setEntryRatioAdd_9(newEntryRatio);
        const regex = 
        /^\d*\.?\d*$/;
        if (regex.test(newEntryRatio)) {
          setEntryRatioAddValid_9(true);
          } 
        else {
          setEntryRatioAddValid_9(false);
          }
        };
      
    /////////////////////////////////////////////////////////////////

    // 진입 USDT 유효성 검사 //// 진입 USDT 유효성 검사 //// 진입 USDT 유효성 검사 //
    const handleEntryUsdt_1 = (e) => {
      const newEntryUsdt = e.target.value.trim();
      setEntryUsdt_1(newEntryUsdt);
        const regex = 
        /^\d*\.?\d*$/;
        if (regex.test(newEntryUsdt)) {
          setEntryUsdtValid_1(true);
          } 
        else {
          setEntryUsdtValid_1(false);
          }
        };
    const handleEntryUsdt_2 = (e) => {
      const newEntryUsdt = e.target.value.trim();
      setEntryUsdt_2(newEntryUsdt);
        const regex = 
        /^\d*\.?\d*$/;
        if (regex.test(newEntryUsdt)) {
          setEntryUsdtValid_2(true);
          } 
        else {
          setEntryUsdtValid_2(false);
          }
        };
    const handleEntryUsdt_3 = (e) => {
      const newEntryUsdt = e.target.value.trim();
      setEntryUsdt_3(newEntryUsdt);
        const regex = 
        /^\d*\.?\d*$/;
        if (regex.test(newEntryUsdt)) {
          setEntryUsdtValid_3(true);
          } 
        else {
          setEntryUsdtValid_3(false);
          }
        };
    const handleEntryUsdt_4 = (e) => {
      const newEntryUsdt = e.target.value.trim();
      setEntryUsdt_4(newEntryUsdt);
        const regex = 
        /^\d*\.?\d*$/;
        if (regex.test(newEntryUsdt)) {
          setEntryUsdtValid_4(true);
          } 
        else {
          setEntryUsdtValid_4(false);
          }
        };
    const handleEntryUsdt_5 = (e) => {
      const newEntryUsdt = e.target.value.trim();
      setEntryUsdt_5(newEntryUsdt);
        const regex = 
        /^\d*\.?\d*$/;
        if (regex.test(newEntryUsdt)) {
          setEntryUsdtValid_5(true);
          } 
        else {
          setEntryUsdtValid_5(false);
          }
        };
    const handleEntryUsdt_6 = (e) => {
      const newEntryUsdt = e.target.value.trim();
      setEntryUsdt_6(newEntryUsdt);
        const regex = 
        /^\d*\.?\d*$/;
        if (regex.test(newEntryUsdt)) {
          setEntryUsdtValid_6(true);
          } 
        else {
          setEntryUsdtValid_6(false);           
          }
        };
    const handleEntryUsdt_7 = (e) => {
      const newEntryUsdt = e.target.value.trim();
      setEntryUsdt_7(newEntryUsdt);
        const regex = 
        /^\d*\.?\d*$/;
        if (regex.test(newEntryUsdt)) {
          setEntryUsdtValid_7(true);
          } 
        else {
          setEntryUsdtValid_7(false);
          }
        };
    const handleEntryUsdt_8 = (e) => {
      const newEntryUsdt = e.target.value.trim();
      setEntryUsdt_8(newEntryUsdt);
        const regex = 
        /^\d*\.?\d*$/;
        if (regex.test(newEntryUsdt)) {
          setEntryUsdtValid_8(true);
          } 
        else {
          setEntryUsdtValid_8(false);
          }
        };
    const handleEntryUsdt_9 = (e) => {
      const newEntryUsdt = e.target.value.trim();
      setEntryUsdt_9(newEntryUsdt);
        const regex = 
        /^\d*\.?\d*$/;
        if (regex.test(newEntryUsdt)) {
          setEntryUsdtValid_9(true);
          } 
        else {
          setEntryUsdtValid_9(false);
          }
        };

        
    const handleEntryRatio_1 = (e) => {
      const newEntryRatio = e.target.value.trim();
      setEntryRatio_1(newEntryRatio);
        const regex = 
        /^\d*\.?\d*$/;
        if (regex.test(newEntryRatio)) {
          setEntryRatioValid_1(true);
          } 
        else {
          setEntryRatioValid_1(false);
          }
        };
    const handleEntryRatio_2 = (e) => {
      const newEntryRatio = e.target.value.trim();
      setEntryRatio_2(newEntryRatio);
        const regex = 
        /^\d*\.?\d*$/;
        if (regex.test(newEntryRatio)) {
          setEntryRatioValid_2(true);
          } 
        else {
          setEntryRatioValid_2(false);
          }
        };
    const handleEntryRatio_3 = (e) => {
      const newEntryRatio = e.target.value.trim();
      setEntryRatio_3(newEntryRatio);
        const regex = 
        /^\d*\.?\d*$/;
        if (regex.test(newEntryRatio)) {
          setEntryRatioValid_3(true);
          } 
        else {
          setEntryRatioValid_3(false);
          }
        };
    const handleEntryRatio_4 = (e) => {
      const newEntryRatio = e.target.value.trim();
      setEntryRatio_4(newEntryRatio);
        const regex = 
        /^\d*\.?\d*$/;
        if (regex.test(newEntryRatio)) {
          setEntryRatioValid_4(true);
          } 
        else {
          setEntryRatioValid_4(false);
          }
        };
    const handleEntryRatio_5 = (e) => {
      const newEntryRatio = e.target.value.trim();
      setEntryRatio_5(newEntryRatio);
        const regex = 
        /^\d*\.?\d*$/;
        if (regex.test(newEntryRatio)) {
          setEntryRatioValid_5(true);
          } 
        else {
          setEntryRatioValid_5(false);
          }
        };
    const handleEntryRatio_6 = (e) => {
      const newEntryRatio = e.target.value.trim();
      setEntryRatio_6(newEntryRatio);
        const regex = 
        /^\d*\.?\d*$/;
        if (regex.test(newEntryRatio)) {
          setEntryRatioValid_6(true);
          } 
        else {
          setEntryRatioValid_6(false);           
          }
        };
    const handleEntryRatio_7 = (e) => {
      const newEntryRatio = e.target.value.trim();
      setEntryRatio_7(newEntryRatio);
        const regex = 
        /^\d*\.?\d*$/;
        if (regex.test(newEntryRatio)) {
          setEntryRatioValid_7(true);
          } 
        else {
          setEntryRatioValid_7(false);
          }
        };
    const handleEntryRatio_8 = (e) => {
      const newEntryRatio = e.target.value.trim();
      setEntryRatio_8(newEntryRatio);
        const regex = 
        /^\d*\.?\d*$/;
        if (regex.test(newEntryRatio)) {
          setEntryRatioValid_8(true);
          } 
        else {
          setEntryRatioValid_8(false);
          }
        };
    const handleEntryRatio_9 = (e) => {
      const newEntryRatio = e.target.value.trim();
      setEntryRatio_9(newEntryRatio);
        const regex = 
        /^\d*\.?\d*$/;
        if (regex.test(newEntryRatio)) {
          setEntryRatioValid_9(true);
          } 
        else {
          setEntryRatioValid_9(false);
          }
        };
      
    const handleAddSymbol = (symbol) => {
      const confirmed = window.confirm(`${symbol}을 리스트에 추가할까요?`);
      if (confirmed) {
      fetch(`/add_user_symbols_wz?symbol=${symbol}`)
      .then(res => res.json())
      .then(data => {
        if (data.msg) {
            alert(data.msg); // msg가 존재할 때만 출력
            return;
          }
        setSymbolList(data.signal)
      })
      .catch(err => console.error(err));
      }
    };

    const deleteSymbol = (symbol) => {
      if (symbol === "default") {
        alert('코인을 먼저 선택하세요!');
        return; // Stop execution if the condition is met
      }
      const confirmed = window.confirm(`${symbol}이 포지션이 걸려 있다면 리스트에서 삭제 후 수동으로 포지션 정리해주세요! ${symbol}을 삭제하시겠습니까?`);
      if (confirmed) {
      fetch(`/delete_user_symbols_wz?symbol=${symbol}`)
      .then(res => res.json())
      .then(data => setSymbolList(data.signal))
      .catch(err => console.error(err));
      removeListItem(symbol, setE_Items);
      removeListItem(symbol, setW_Items);
      setSymbol('default')
      }
    };

    const handleOpenPopup = (event) => {
      const rect = event.currentTarget.getBoundingClientRect();
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
      const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
      const popupWidth = 300;
      const popupHeight = 320;

      const desiredTop = rect.top + scrollTop - popupHeight - 16;
      const top = Math.max(scrollTop + 20, desiredTop);
      const minLeft = scrollLeft + 16;
      const maxLeft = scrollLeft + viewportWidth - popupWidth - 16;
      const candidateLeft = rect.left + scrollLeft;
      const left = Math.min(Math.max(candidateLeft, minLeft), maxLeft);

      setPopupAnchor({ top, left });
      setIsPopupOpen(true);
    };

    const handleClosePopup = () => {
      setIsPopupOpen(false);
      setPopupAnchor(null);
    };

    const toggleSymbolMenu = () => {
      setIsSymbolMenuOpen((prev) => !prev);
    };

    const handleSymbolSelect = (value) => {
      setSymbol(value);
      if (value && value !== 'default') {
        onClickSearch(value);
      }
      setIsSymbolMenuOpen(false);
    };        

    /////////////////////////////////////////////////////////////////
    
    // const handleCoverratio = (e) => {
    //   const newCoverRatio = e.target.value.trim();
    //   setCoverRatio(newCoverRatio);
    //     const regex = 
    //     /^\d*\.?\d*$/;
    //     if (regex.test(newCoverRatio)) {
    //       setCoverratioValid(true);
    //       } 
    //     else {
    //       setCoverratioValid(false);
    //       }
    //     };

    const addListItem = (newItem, listSetter) => {
      listSetter(prevList => [...prevList, newItem]);
    };

    const removeListItem = (itemName, listSetter) => {
      listSetter(prevList => prevList.filter(item => item !== itemName));
    };
    
    const onClickCoin = (coin) => {
      setSymbol(coin);
      onClickSearch(coin);
    }

    useEffect(() => {

      if(firstusdtValid && leverageValid && sensitivityValid && atrperiodValid && sensitivityStandardValid && sensitivityCloseValid && atrperiodStandardValid && atrperiodCloseValid &&
        profitValid && losscutValid && ma_1stValid && ma_2ndValid && ma_3rdValid && 
        rsi_lengthValid && low_rsiValid && high_rsitValid && min_benefit_ratioratioValid && emaStandardValid &&
        both_benefit_ratioratioValid && mixBenefitValid && total_benefit_usdtValid && entryusdtValid_1 && entryusdtValid_2 && entryusdtValid_3 && entryusdtValid_4 &&
        entryusdtValid_5 && entryusdtValid_6 && entryusdtValid_7 && entryusdtValid_8 && entryusdtValid_9 &&
        entryratioValid_1 && entryratioValid_2 && entryratioValid_3 && entryratioValid_4 &&
        entryratioValid_5 && entryratioValid_6 && entryratioValid_7 && entryratioValid_8 && 
        entryratioValid_9 && entryusdtAddValid_1 && entryusdtAddValid_2 && entryusdtAddValid_3 && entryusdtAddValid_4 &&
        entryusdtAddValid_5 && entryusdtAddValid_6 && entryusdtAddValid_7 && entryusdtAddValid_8 && entryusdtAddValid_9 &&
        entryratioAddValid_1 && entryratioAddValid_2 && entryratioAddValid_3 && entryratioAddValid_4 &&
        entryratioAddValid_5 && entryratioAddValid_6 && entryratioAddValid_7 && entryratioAddValid_8 && 
        entryratioAddValid_9) {
        setNotAllow(false);
        return;
      }
      setNotAllow(true);
    }, [firstusdtValid, leverageValid, sensitivityValid, atrperiodValid, sensitivityCloseValid, atrperiodCloseValid, profitValid, losscutValid, ma_1stValid, ma_2ndValid, ma_3rdValid,
      rsi_lengthValid, low_rsiValid, high_rsitValid, min_benefit_ratioratioValid, emaStandardValid, sensitivityStandardValid, atrperiodStandardValid,
      both_benefit_ratioratioValid, mixBenefitValid, total_benefit_usdtValid, entryusdtValid_1, entryusdtValid_2, entryusdtValid_3, entryusdtValid_4,
      entryusdtValid_5, entryusdtValid_6, entryusdtValid_7, entryusdtValid_8, entryusdtValid_9, entryratioValid_1, entryratioValid_2,
      entryratioValid_3, entryratioValid_4, entryratioValid_5, entryratioValid_6, entryratioValid_7, entryratioValid_8, 
      entryratioValid_9, entryusdtAddValid_1, entryusdtAddValid_2, entryusdtAddValid_3, entryusdtAddValid_4,
      entryusdtAddValid_5, entryusdtAddValid_6, entryusdtAddValid_7, entryusdtAddValid_8, entryusdtAddValid_9, entryratioAddValid_1, entryratioAddValid_2,
      entryratioAddValid_3, entryratioAddValid_4, entryratioAddValid_5, entryratioAddValid_6, entryratioAddValid_7, entryratioAddValid_8, 
      entryratioAddValid_9]);

    useEffect(() => {
        fetch(`/get_user_symbols_wz`)
        .then(res => res.json())
        .then(data => setSymbolList(data.signal))
        .catch(err => console.error(err));
    },[symbol,uid]);

    const onClickSamaria_check = () => {
      window.open('/SamariaSignals', '_blank');
      return;
    }

    const onClickSearch = (symbol) => {
      let urlName
      const str = symbol;
      console.log(str);
      if(str === 'default'){
        urlName = 'default'
      }else{
        const index = str.indexOf('/');
        urlName = str.substring(0,index);
      }  
      fetch(`/setdata_wz/${urlName}/${setlevel}`)
      .then((response) => {
        if(!response.ok) {
          throw new Error('400 아니면 500 에러남');
        }
        return response.json()
      })
      .then((result) => {
        console.log('db에서 성공적으로 데이터 받아옴');
        setEntryMode(result[0].entry_mode);
        setEntrySignal(result[0].entry_signal);
        setEmaStandard(result[0].ema_standard);
        setEmaLosscut(result[0].ema_losscut);
        setEmaEntryGapRatio(
          result[0].ema_entry_gap_ratio != null ? Number(result[0].ema_entry_gap_ratio) : 0
        );

        setFirstUsdt(result[0].first_usdt);
        setLeverage(result[0].leverage_info);
        setEntryCandle(result[0].entry_candle);

        setAddposition(result[0].add_position);
        setAddsignals(result[0].add_signals);

        setEntryChecking(result[0].entry_checking);
        setStandardChecking(result[0].standard_checking);
        setBenefitChecking(result[0].benefit_checking);

        setSingleDoubleBenefit(result[0].single_double_benefit);
        setBenefitStandard(result[0].benefit_standard);

        setEntryStandard(result[0].entry_standard);

        setEntryStandardType(result[0].entry_standard_type);
        setEntryStandardCandle(result[0].entry_standard_candle);
    
        setSensitivityStandard(result[0].sensitivity_standard);
        setAtrPeriodStandard(result[0].atr_period_standard);

        setSensitivity(result[0].sensitivity);
        setAtrPeriod(result[0].atr_period);

        setSensitivityClose(result[0].sensitivity_close);
        setAtrPeriodClose(result[0].atr_period_close);

        setProfit(result[0].profit);
        setLosscut(result[0].losscut);
        
        setMa1st(result[0].ma_1st);
        setMa2nd(result[0].ma_2nd);
        setMa3rd(result[0].ma_3rd);
        setRsiLength(result[0].rsi_length);
        setLowRsi(result[0].low_rsi);
        setHighRsi(result[0].high_rsi);
        setMixCandle(result[0].mix_candle);
        setCloseCandle(result[0].close_candle);
        setDirection(result[0].direction);

        setMinBenefitRatio(result[0].min_benefit_ratio);
        setBothBenefitRatio(result[0].both_benefit_ratio);
        setMixbenefit(result[0].mix_benefit);
        setMixBenefitReentry(result[0].mix_benefit_reentry === 'apply' ? 'apply' : 'off');
        setOccurrenceLosscut(result[0].occurrence_losscut === 'apply' ? 'apply' : 'off');
        setTotalBenefitUsdt(result[0].total_benefit_usdt);
        setTotalBenefitUsdtObj(result[0].total_benefit_usdt_obj);

        setLongCount(result[0].long_count);
        setShortCount(result[0].short_count);

        setEntryUsdt_1(result[0].entry_usdt_1);
        setEntryUsdt_2(result[0].entry_usdt_2);
        setEntryUsdt_3(result[0].entry_usdt_3);
        setEntryUsdt_4(result[0].entry_usdt_4);
        setEntryUsdt_5(result[0].entry_usdt_5);
        setEntryUsdt_6(result[0].entry_usdt_6);
        setEntryUsdt_7(result[0].entry_usdt_7);
        setEntryUsdt_8(result[0].entry_usdt_8);
        setEntryUsdt_9(result[0].entry_usdt_9);

        setEntryRatio_1(result[0].entry_ratio_1);
        setEntryRatio_2(result[0].entry_ratio_2);
        setEntryRatio_3(result[0].entry_ratio_3);
        setEntryRatio_4(result[0].entry_ratio_4);
        setEntryRatio_5(result[0].entry_ratio_5);
        setEntryRatio_6(result[0].entry_ratio_6);
        setEntryRatio_7(result[0].entry_ratio_7);
        setEntryRatio_8(result[0].entry_ratio_8);
        setEntryRatio_9(result[0].entry_ratio_9);
      
        setEntryUsdtAdd_1(result[0].entry_usdt_add_1);
        setEntryUsdtAdd_2(result[0].entry_usdt_add_2);
        setEntryUsdtAdd_3(result[0].entry_usdt_add_3);
        setEntryUsdtAdd_4(result[0].entry_usdt_add_4);
        setEntryUsdtAdd_5(result[0].entry_usdt_add_5);
        setEntryUsdtAdd_6(result[0].entry_usdt_add_6);
        setEntryUsdtAdd_7(result[0].entry_usdt_add_7);
        setEntryUsdtAdd_8(result[0].entry_usdt_add_8);
        setEntryUsdtAdd_9(result[0].entry_usdt_add_9);

        setEntryRatioAdd_1(result[0].entry_ratio_add_1);
        setEntryRatioAdd_2(result[0].entry_ratio_add_2);
        setEntryRatioAdd_3(result[0].entry_ratio_add_3);
        setEntryRatioAdd_4(result[0].entry_ratio_add_4);
        setEntryRatioAdd_5(result[0].entry_ratio_add_5);
        setEntryRatioAdd_6(result[0].entry_ratio_add_6);
        setEntryRatioAdd_7(result[0].entry_ratio_add_7);
        setEntryRatioAdd_8(result[0].entry_ratio_add_8);
        setEntryRatioAdd_9(result[0].entry_ratio_add_9);

        setCandleTypeEntry(result[0].candle_type_entry);
        setCandleTypeClose(result[0].candle_type_close);

        setTradeStart(result[0].trade_start);
        setBenefit_stop(result[0].current_symbol_stop);
        setCurrentCoin_stop(result[0].current_coin_clear);
        setAllCoin_stop(result[0].all_coin_clear);
        setEnd(result[0].end);

        const e_checklist = isItemInList(result[0].symbol, e_items);
        const w_checklist = isItemInList(result[0].symbol, w_items);
        
        if(str !== 'default'){
          result.forEach(item => {
            // console.log('item',item)
            if ((item.trade_start === 1 || item.trade_start === '1') && e_checklist === false) {
              addListItem(item.symbol, setE_Items);
            }
            if ((item.trade_start === 0 || item.trade_start === '0') && e_checklist === true) {
              removeListItem(item.symbol, setE_Items);
            }
          
            // Waiting coin 리스트 삽입 및 삭제
            if ((item.current_symbol_stop === 1 || item.current_symbol_stop === '1') && w_checklist === false) {
              addListItem(item.symbol, setW_Items);
            }
            if ((item.current_symbol_stop === 0 || item.current_symbol_stop === '0') && w_checklist === true) {
              removeListItem(item.symbol, setW_Items);
            }
          });
        };

        console.log('e_items',e_items);
        console.log('e_checklist',e_checklist);
      })
      .catch((error) => {
        console.log('에러남');
      })
    }

    const onClickStart = () => {

      if (symbol === "default") {
        alert('코인을 먼저 선택하세요!');
        return; // Stop execution if the condition is met
      }
      
      let confirmed = false

      if (trade_start === 0 || trade_start === "0"){
        confirmed = window.confirm(symbol + '의 매매를 시작하시겠습니까?');
      }else if (trade_start === 1 || trade_start === "1"){
        confirmed = window.confirm(symbol + '의 셋팅값을 변경하시겠습니까?');
      }

      if (confirmed) {
        console.log('스타트 버튼 클릭')
        fetch('/start_wz', {
          method : 'POST',
          headers: {
            'Content-Type' : 'application/json',
          },
          body: JSON.stringify({
            symbol,
            entry_mode,
            first_usdt,
            leverage_info,
            entry_standard,
            entry_signal,
            ema_standard,
            ema_losscut,
            ema_entry_gap_ratio,
            entry_standard_type,
            entry_standard_candle,
            sensitivity_standard,
            atr_period_standard,
            sensitivity,
            atr_period,
            sensitivity_close,
            atr_period_close,
            profit,
            losscut,
            entry_candle,
            add_position,
            single_double_benefit,
            benefit_standard,
            add_signals,
            entry_checking,
            standard_checking,
            benefit_checking,
            ma_1st,
            ma_2nd,
            ma_3rd,
            rsi_length,
            low_rsi,
            high_rsi,
            mix_candle,
            close_candle,
            direction,
            min_benefit_ratio,
            both_benefit_ratio,
            mix_benefit,
            mix_benefit_reentry,
            occurrence_losscut,
            total_benefit_usdt,
            entry_usdt_1,
            entry_usdt_2,
            entry_usdt_3,
            entry_usdt_4,
            entry_usdt_5,
            entry_usdt_6,
            entry_usdt_7,
            entry_usdt_8,
            entry_usdt_9,
            entry_ratio_1,
            entry_ratio_2,
            entry_ratio_3,
            entry_ratio_4,
            entry_ratio_5,
            entry_ratio_6,
            entry_ratio_7,
            entry_ratio_8,
            entry_ratio_9,
            entry_usdt_add_1,
            entry_usdt_add_2,
            entry_usdt_add_3,
            entry_usdt_add_4,
            entry_usdt_add_5,
            entry_usdt_add_6,
            entry_usdt_add_7,
            entry_usdt_add_8,
            entry_usdt_add_9,
            entry_ratio_add_1,
            entry_ratio_add_2,
            entry_ratio_add_3,
            entry_ratio_add_4,
            entry_ratio_add_5,
            entry_ratio_add_6,
            entry_ratio_add_7,
            entry_ratio_add_8,
            entry_ratio_add_9,
            candle_type_entry,
            candle_type_close
            }),
         })
         .then(response => response.json())  // 응답을 JSON 형식으로 변환
         .then(data => {
          onClickSearch(symbol);
          alert(data.message);  // JSON에서 message 값을 가져옴
         })
         .catch(error => console.error('Error:', error));
       };
    };

    const buildWzPresetPayload = () => ({
      entry_mode,
      first_usdt,
      leverage_info,
      entry_standard,
      entry_signal,
      ema_standard,
      ema_losscut,
      ema_entry_gap_ratio,
      entry_standard_type,
      entry_standard_candle,
      sensitivity_standard,
      atr_period_standard,
      sensitivity,
      atr_period,
      sensitivity_close,
      atr_period_close,
      profit,
      losscut,
      entry_candle,
      add_position,
      single_double_benefit,
      benefit_standard,
      add_signals,
      entry_checking,
      standard_checking,
      benefit_checking,
      ma_1st,
      ma_2nd,
      ma_3rd,
      rsi_length,
      low_rsi,
      high_rsi,
      mix_candle,
      close_candle,
      direction,
      min_benefit_ratio,
      both_benefit_ratio,
      mix_benefit,
      mix_benefit_reentry,
      occurrence_losscut,
      total_benefit_usdt,
      entry_usdt_1,
      entry_usdt_2,
      entry_usdt_3,
      entry_usdt_4,
      entry_usdt_5,
      entry_usdt_6,
      entry_usdt_7,
      entry_usdt_8,
      entry_usdt_9,
      entry_ratio_1,
      entry_ratio_2,
      entry_ratio_3,
      entry_ratio_4,
      entry_ratio_5,
      entry_ratio_6,
      entry_ratio_7,
      entry_ratio_8,
      entry_ratio_9,
      entry_usdt_add_1,
      entry_usdt_add_2,
      entry_usdt_add_3,
      entry_usdt_add_4,
      entry_usdt_add_5,
      entry_usdt_add_6,
      entry_usdt_add_7,
      entry_usdt_add_8,
      entry_usdt_add_9,
      entry_ratio_add_1,
      entry_ratio_add_2,
      entry_ratio_add_3,
      entry_ratio_add_4,
      entry_ratio_add_5,
      entry_ratio_add_6,
      entry_ratio_add_7,
      entry_ratio_add_8,
      entry_ratio_add_9,
      candle_type_entry,
      candle_type_close,
    });

    const applyWzPreset = (payload = {}) => {
      if (payload.entry_mode !== undefined) setEntryMode(payload.entry_mode);
      if (payload.first_usdt !== undefined) setFirstUsdt(payload.first_usdt);
      if (payload.leverage_info !== undefined) setLeverage(payload.leverage_info);
      if (payload.entry_standard !== undefined) setEntryStandard(payload.entry_standard);
      if (payload.entry_signal !== undefined) setEntrySignal(payload.entry_signal);
      if (payload.ema_standard !== undefined) setEmaStandard(payload.ema_standard);
      if (payload.ema_losscut !== undefined) setEmaLosscut(payload.ema_losscut);
      if (payload.ema_entry_gap_ratio !== undefined) {
        setEmaEntryGapRatio(Number(payload.ema_entry_gap_ratio) || 0);
      }
      if (payload.entry_standard_type !== undefined) setEntryStandardType(payload.entry_standard_type);
      if (payload.entry_standard_candle !== undefined) setEntryStandardCandle(payload.entry_standard_candle);
      if (payload.sensitivity_standard !== undefined) setSensitivityStandard(payload.sensitivity_standard);
      if (payload.atr_period_standard !== undefined) setAtrPeriodStandard(payload.atr_period_standard);
      if (payload.sensitivity !== undefined) setSensitivity(payload.sensitivity);
      if (payload.atr_period !== undefined) setAtrPeriod(payload.atr_period);
      if (payload.sensitivity_close !== undefined) setSensitivityClose(payload.sensitivity_close);
      if (payload.atr_period_close !== undefined) setAtrPeriodClose(payload.atr_period_close);
      if (payload.profit !== undefined) setProfit(payload.profit);
      if (payload.losscut !== undefined) setLosscut(payload.losscut);
      if (payload.entry_candle !== undefined) setEntryCandle(payload.entry_candle);
      if (payload.add_position !== undefined) setAddposition(payload.add_position);
      if (payload.single_double_benefit !== undefined) setSingleDoubleBenefit(payload.single_double_benefit);
      if (payload.benefit_standard !== undefined) setBenefitStandard(payload.benefit_standard);
      if (payload.add_signals !== undefined) setAddsignals(payload.add_signals);
      if (payload.entry_checking !== undefined) setEntryChecking(payload.entry_checking);
      if (payload.standard_checking !== undefined) setStandardChecking(payload.standard_checking);
      if (payload.benefit_checking !== undefined) setBenefitChecking(payload.benefit_checking);
      if (payload.ma_1st !== undefined) setMa1st(payload.ma_1st);
      if (payload.ma_2nd !== undefined) setMa2nd(payload.ma_2nd);
      if (payload.ma_3rd !== undefined) setMa3rd(payload.ma_3rd);
      if (payload.rsi_length !== undefined) setRsiLength(payload.rsi_length);
      if (payload.low_rsi !== undefined) setLowRsi(payload.low_rsi);
      if (payload.high_rsi !== undefined) setHighRsi(payload.high_rsi);
      if (payload.mix_candle !== undefined) setMixCandle(payload.mix_candle);
      if (payload.close_candle !== undefined) setCloseCandle(payload.close_candle);
      if (payload.direction !== undefined) setDirection(payload.direction);
      if (payload.min_benefit_ratio !== undefined) setMinBenefitRatio(payload.min_benefit_ratio);
      if (payload.both_benefit_ratio !== undefined) setBothBenefitRatio(payload.both_benefit_ratio);
      if (payload.mix_benefit !== undefined) setMixbenefit(payload.mix_benefit);
      if (payload.mix_benefit_reentry !== undefined) {
        setMixBenefitReentry(payload.mix_benefit_reentry === 'apply' ? 'apply' : 'off');
      }
      if (payload.occurrence_losscut !== undefined) {
        const occ = payload.occurrence_losscut === 'apply' ? 'apply' : 'off';
        setOccurrenceLosscut(occ);
        if (occ === 'apply') {
          setMixBenefitReentry('off');
        }
      }
      if (payload.total_benefit_usdt !== undefined) setTotalBenefitUsdt(payload.total_benefit_usdt);
      if (payload.entry_usdt_1 !== undefined) setEntryUsdt_1(payload.entry_usdt_1);
      if (payload.entry_usdt_2 !== undefined) setEntryUsdt_2(payload.entry_usdt_2);
      if (payload.entry_usdt_3 !== undefined) setEntryUsdt_3(payload.entry_usdt_3);
      if (payload.entry_usdt_4 !== undefined) setEntryUsdt_4(payload.entry_usdt_4);
      if (payload.entry_usdt_5 !== undefined) setEntryUsdt_5(payload.entry_usdt_5);
      if (payload.entry_usdt_6 !== undefined) setEntryUsdt_6(payload.entry_usdt_6);
      if (payload.entry_usdt_7 !== undefined) setEntryUsdt_7(payload.entry_usdt_7);
      if (payload.entry_usdt_8 !== undefined) setEntryUsdt_8(payload.entry_usdt_8);
      if (payload.entry_usdt_9 !== undefined) setEntryUsdt_9(payload.entry_usdt_9);
      if (payload.entry_ratio_1 !== undefined) setEntryRatio_1(payload.entry_ratio_1);
      if (payload.entry_ratio_2 !== undefined) setEntryRatio_2(payload.entry_ratio_2);
      if (payload.entry_ratio_3 !== undefined) setEntryRatio_3(payload.entry_ratio_3);
      if (payload.entry_ratio_4 !== undefined) setEntryRatio_4(payload.entry_ratio_4);
      if (payload.entry_ratio_5 !== undefined) setEntryRatio_5(payload.entry_ratio_5);
      if (payload.entry_ratio_6 !== undefined) setEntryRatio_6(payload.entry_ratio_6);
      if (payload.entry_ratio_7 !== undefined) setEntryRatio_7(payload.entry_ratio_7);
      if (payload.entry_ratio_8 !== undefined) setEntryRatio_8(payload.entry_ratio_8);
      if (payload.entry_ratio_9 !== undefined) setEntryRatio_9(payload.entry_ratio_9);
      if (payload.entry_usdt_add_1 !== undefined) setEntryUsdtAdd_1(payload.entry_usdt_add_1);
      if (payload.entry_usdt_add_2 !== undefined) setEntryUsdtAdd_2(payload.entry_usdt_add_2);
      if (payload.entry_usdt_add_3 !== undefined) setEntryUsdtAdd_3(payload.entry_usdt_add_3);
      if (payload.entry_usdt_add_4 !== undefined) setEntryUsdtAdd_4(payload.entry_usdt_add_4);
      if (payload.entry_usdt_add_5 !== undefined) setEntryUsdtAdd_5(payload.entry_usdt_add_5);
      if (payload.entry_usdt_add_6 !== undefined) setEntryUsdtAdd_6(payload.entry_usdt_add_6);
      if (payload.entry_usdt_add_7 !== undefined) setEntryUsdtAdd_7(payload.entry_usdt_add_7);
      if (payload.entry_usdt_add_8 !== undefined) setEntryUsdtAdd_8(payload.entry_usdt_add_8);
      if (payload.entry_usdt_add_9 !== undefined) setEntryUsdtAdd_9(payload.entry_usdt_add_9);
      if (payload.entry_ratio_add_1 !== undefined) setEntryRatioAdd_1(payload.entry_ratio_add_1);
      if (payload.entry_ratio_add_2 !== undefined) setEntryRatioAdd_2(payload.entry_ratio_add_2);
      if (payload.entry_ratio_add_3 !== undefined) setEntryRatioAdd_3(payload.entry_ratio_add_3);
      if (payload.entry_ratio_add_4 !== undefined) setEntryRatioAdd_4(payload.entry_ratio_add_4);
      if (payload.entry_ratio_add_5 !== undefined) setEntryRatioAdd_5(payload.entry_ratio_add_5);
      if (payload.entry_ratio_add_6 !== undefined) setEntryRatioAdd_6(payload.entry_ratio_add_6);
      if (payload.entry_ratio_add_7 !== undefined) setEntryRatioAdd_7(payload.entry_ratio_add_7);
      if (payload.entry_ratio_add_8 !== undefined) setEntryRatioAdd_8(payload.entry_ratio_add_8);
      if (payload.entry_ratio_add_9 !== undefined) setEntryRatioAdd_9(payload.entry_ratio_add_9);
      if (payload.candle_type_entry !== undefined) setCandleTypeEntry(payload.candle_type_entry);
      if (payload.candle_type_close !== undefined) setCandleTypeClose(payload.candle_type_close);
    };

    // 종합 익절 시작
    const onClickTotal_start = (symbol) => {
      let confirmed

      const benefitValue = parseFloat(total_benefit_usdt);

      if (isNaN(benefitValue) || benefitValue === 0) {
        confirmed = window.confirm('종합 익절 값을 넣어주세요!');
      } else {
        confirmed = window.confirm('종합 익절을 시작할까요? 혹시라도 추가 입금시에는 종합익절을 취소한 뒤 진행하세요');
      }

      if (confirmed) {
        const usdt = total_benefit_usdt;
        fetch(`/setdata_total_benefit_wz/${usdt}`)
        .then((response) => {
          if(!response.ok) {
            throw new Error('400 아니면 500 에러남');
          }
          return response.json()
        })
        .then((result) => {
          console.log('total_benefit에서 받아온 데이터');
          // 🔹 서버에서 보내준 메시지를 사용자에게 보여주기
          if (result.message) {
            alert(result.message);
          }
          onClickSearch(symbol);
        })
        .catch((error) => {
          console.log('에러남');
          })
        };
    }

    // 종합 익절 취소 
    const onClickTotal_cancel = (symbol) => {
      const confirmed = window.confirm('종합 익절을 취소할까요?');

      if (confirmed) {
        fetch(`/setdata_total_benefit_cancel_wz`)
        .then((response) => {
          if(!response.ok) {
            throw new Error('400 아니면 500 에러남');
          }
          return response.json()
        })
        .then((result) => {
          console.log('total_benefit_cancel에서 받아온 데이터');
          // 🔹 서버에서 보내준 메시지를 사용자에게 보여주기
          if (result.message) {
            alert(result.message);
          }
          onClickSearch(symbol);
        })
        .catch((error) => {
          console.log('에러남');
        })
      };
    }

    // 익절 후 정리
    const onClickBenefit_stop = (symbol) => {
      const confirmed = window.confirm('Do you want to clean up the selected coin after profiting?');

      if (confirmed) {
        setBenefit_stop(1);
        let urlName
        const str = symbol;
        console.log(str);
        if(str === 'default'){
          urlName = 'default'
        }else{
          const index = str.indexOf('/');
          urlName = str.substring(0,index);
        }
        fetch(`/setdata_benefit_wz/${urlName}/${setlevel}`)
        .then((response) => {
          if(!response.ok) {
            throw new Error('400 아니면 500 에러남');
          }
          return response.json()
        })
        .then((result) => {
          console.log('benefit에서 받아온 데이터');
          onClickSearch(symbol);
        })
        .catch((error) => {
          console.log('에러남');
          })
        };
    }

    // 익절 후 정리 취소 
    const onClickBenefit_cancel = (symbol) => {
      const confirmed = window.confirm('Do you want to cancel clearing after profiting the selected coins?');

      if (confirmed) {
        setBenefit_cancel(1);
        let urlName
        const str = symbol;
        console.log(str);
        if(str === 'default'){
          urlName = 'default'
        }else{
          const index = str.indexOf('/');
          urlName = str.substring(0,index);
        }
        fetch(`/setdata_benefit_cancel_wz/${urlName}/${setlevel}`)
        .then((response) => {
          if(!response.ok) {
            throw new Error('400 아니면 500 에러남');
          }
          return response.json()
        })
        .then((result) => {
          console.log('benefit_cancel에서 받아온 데이터');
          onClickSearch(symbol);
        })
        .catch((error) => {
          console.log('에러남');
        })
      };
    }

    // 현재 코인 정리 
    const onClickCurrentCoin_stop = (symbol) => {
      const confirmed = window.confirm('현재 선택하신 코인의 포지션을 정리하시길 원하십니까? 포지션 정리시 약 5~10초 정도 지연될 수도 있습니다.');

      if (confirmed) {
        setCurrentCoin_stop(true);
        const str = symbol;
        console.log(str);
        const index = str.indexOf('/');
        const urlName = str.substring(0,index);
        fetch(`/setdata_current_coin_clear_wz/${urlName}`)
        .then((response) => {
          if(!response.ok) {
            throw new Error('400 아니면 500 에러남');
          }
          return response.json()
        })
        .then((result) => {
          console.log('current_stop에서 받아온 데이터');
          onClickSearch(symbol);
        })
        .catch((error) => {
          console.log('에러남');
        })
      };
    }

    // 현재 코인 롱 정리 
    const onClickCurrentCoin_long_stop = (symbol) => {
      const confirmed = window.confirm('현재 선택하신 코인의 롱 포지션을 정리하시길 원하십니까? 포지션 정리시 약 5~10초 정도 지연될 수도 있습니다.');

      if (confirmed) {
        setCurrentCoin_stop(true);
        const str = symbol;
        console.log(str);
        const index = str.indexOf('/');
        const urlName = str.substring(0,index);
        fetch(`/setdata_current_coin_long_clear_wz/${urlName}`)
        .then((response) => {
          if(!response.ok) {
            throw new Error('400 아니면 500 에러남');
          }
          return response.json()
        })
        .then((result) => {
          console.log('current_stop에서 받아온 데이터');
          onClickSearch(symbol);
        })
        .catch((error) => {
          console.log('에러남');
        })
      };
    }

    // 현재 코인 정리 
    const onClickCurrentCoin_short_stop = (symbol) => {
      const confirmed = window.confirm('현재 선택하신 코인의 숏 포지션을 정리하시길 원하십니까? 포지션 정리시 약 5~10초 정도 지연될 수도 있습니다.');

      if (confirmed) {
        setCurrentCoin_stop(true);
        const str = symbol;
        console.log(str);
        const index = str.indexOf('/');
        const urlName = str.substring(0,index);
        fetch(`/setdata_current_coin_short_clear_wz/${urlName}`)
        .then((response) => {
          if(!response.ok) {
            throw new Error('400 아니면 500 에러남');
          }
          return response.json()
        })
        .then((result) => {
          console.log('current_stop에서 받아온 데이터');
          onClickSearch(symbol);
        })
        .catch((error) => {
          console.log('에러남');
        })
      };
    }

    //진입중인 모든 코인 정리
    const onClickAllCoin_stop = () => {
      const confirmed = window.confirm('Do you really want to liquidate all coins that are currently entering the market?');

      if (confirmed) {
        setAllCoin_stop(true);
        const str = symbol;
        console.log(str);
        const index = str.indexOf('/');
        const urlName = str.substring(0,index);
        fetch(`/setdata_all_coin_clear_wz/${urlName}`)
        .then((response) => {
          if(!response.ok) {
            throw new Error('400 아니면 500 에러남');
          }
          return response.json()
        })
        .then((result) => {
          console.log('current_stop에서 받아온 데이터');
          onClickSearch(symbol);
        })
        .catch((error) => {
          console.log('에러남');
        })
      };
    }

    //로그아웃
    const onClickEnd = () => {
        const uuid = cookies.uuid; // cookies에서 uuid를 얻어옴
        if (!uuid) {
          alert('로그인 정보가 존재하지 않습니다.');
          return;
        }
        // 로그아웃 요청을 서버로 보냅니다.
        fetch('/logout', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ userId: uuid })
        })
        .then(response => {
          if (!response.ok) {
            throw new Error('로그아웃 요청 실패');
          }
          console.log('로그아웃 성공');
          alert('Logout is successful.');
          window.location.href = '/';
        })
        .catch(error => {
          console.error('로그아웃 요청 중 오류 발생:', error);
        });
      // UUID를 서버로 보내는 예시 코드
    }

    // add_position 값이 변경될 때마다 텍스트 업데이트
    useEffect(() => {
      if (add_position === "1" || add_position === 1) {
        setCandleText("맥스KP익절 기준봉")
        setLeverageText("총진입 횟수");
        setLeverageText_short("");
        setBenefitText("단일 익절률(%)")
        setSetText_long("맥스 셋팅");
        setSetText_short("");
      } else if(add_position === "0" || add_position === 0){
        setCandleText("진입 시간봉")
        setLeverageText("롱진입 횟수"); // 기본값 설정
        setLeverageText_short("숏진입 횟수"); // 기본값 설정
        setBenefitText("양전 익절률(%)")
        setSetText_long("");
        setSetText_short("오리지널 셋팅");
      } else if(add_position === "2" || add_position === 2){
        setCandleText("진입 시간봉")
        setLeverageText("롱진입 횟수"); // 기본값 설정
        setLeverageText_short("숏진입 횟수"); // 기본값 설정
        setBenefitText("단일 익절률(%)")
        setSetText_long("롱진입 셋팅");
        setSetText_short("숏진입 셋팅");
      }
    }, [add_position]);

    const entryLabels = ["1st", "2nd", "3rd", "4th", "5th", "6th", "7th", "8th", "9th"];

    const longUsdtValues = [entry_usdt_add_1, entry_usdt_add_2, entry_usdt_add_3, entry_usdt_add_4, entry_usdt_add_5, entry_usdt_add_6, entry_usdt_add_7, entry_usdt_add_8, entry_usdt_add_9];
    const longUsdtHandlers = [handleEntryUsdtAdd_1, handleEntryUsdtAdd_2, handleEntryUsdtAdd_3, handleEntryUsdtAdd_4, handleEntryUsdtAdd_5, handleEntryUsdtAdd_6, handleEntryUsdtAdd_7, handleEntryUsdtAdd_8, handleEntryUsdtAdd_9];
    const longRatioValues = [entry_ratio_add_1, entry_ratio_add_2, entry_ratio_add_3, entry_ratio_add_4, entry_ratio_add_5, entry_ratio_add_6, entry_ratio_add_7, entry_ratio_add_8, entry_ratio_add_9];
    const longRatioHandlers = [handleEntryRatioAdd_1, handleEntryRatioAdd_2, handleEntryRatioAdd_3, handleEntryRatioAdd_4, handleEntryRatioAdd_5, handleEntryRatioAdd_6, handleEntryRatioAdd_7, handleEntryRatioAdd_8, handleEntryRatioAdd_9];

    const shortUsdtValues = [entry_usdt_1, entry_usdt_2, entry_usdt_3, entry_usdt_4, entry_usdt_5, entry_usdt_6, entry_usdt_7, entry_usdt_8, entry_usdt_9];
    const shortUsdtHandlers = [handleEntryUsdt_1, handleEntryUsdt_2, handleEntryUsdt_3, handleEntryUsdt_4, handleEntryUsdt_5, handleEntryUsdt_6, handleEntryUsdt_7, handleEntryUsdt_8, handleEntryUsdt_9];
    const shortRatioValues = [entry_ratio_1, entry_ratio_2, entry_ratio_3, entry_ratio_4, entry_ratio_5, entry_ratio_6, entry_ratio_7, entry_ratio_8, entry_ratio_9];
    const shortRatioHandlers = [handleEntryRatio_1, handleEntryRatio_2, handleEntryRatio_3, handleEntryRatio_4, handleEntryRatio_5, handleEntryRatio_6, handleEntryRatio_7, handleEntryRatio_8, handleEntryRatio_9];

    const entryRows = entryLabels.map((label, idx) => ({
      label,
      longUsdt: longUsdtValues[idx],
      longRatio: longRatioValues[idx],
      onLongUsdtChange: longUsdtHandlers[idx],
      onLongRatioChange: longRatioHandlers[idx],
      shortUsdt: shortUsdtValues[idx],
      shortRatio: shortRatioValues[idx],
      onShortUsdtChange: shortUsdtHandlers[idx],
      onShortRatioChange: shortRatioHandlers[idx],
    }));

    return (
      <main className="kp-console kp-console--wz">
        <div className="kp-console__glare" aria-hidden="true" />
        <section className="kp-console__panel">
          <header className="kp-hero">
            <p className="kp-hero__eyebrow">Bitgenstone auto trade system</p>
            <h1 className="kp-hero__title">Wizard cockpit</h1>
            <p className="kp-hero__subtitle">
              Wizard 전략의 다층 조건과 보조 시그널을 한 화면에서 제어하세요. UID 상태와 실시간 리스트로 포지션 흐름을 즉시 확인할 수 있습니다.
            </p>
            <div className="kp-hero__meta">
              <span className="kp-hero__uid">UID {uid || "—"}</span>
              <span>{nickname ? `${nickname}님 환영합니다` : "계정 정보를 불러오는 중"}</span>
            </div>
          </header>

          <div className="kp-console__layout">
            <div className="kp-console__main">
              <article className="kp-card kp-card--controls">
                <div className="kp-symbol-controls">
                  <div className="kp-symbol-dropdown" ref={symbolDropdownRef}>
                    <button
                      type="button"
                      className={`kp-symbol-dropdown__trigger ${isSymbolMenuOpen ? "is-open" : ""}`}
                      onClick={toggleSymbolMenu}
                      aria-haspopup="listbox"
                      aria-expanded={isSymbolMenuOpen}
                    >
                      <span className="kp-symbol-dropdown__current">{currentSymbolLabel}</span>
                      <span className="kp-symbol-dropdown__chevron" aria-hidden="true" />
                    </button>
                    {isSymbolMenuOpen && (
                      <div className="kp-symbol-dropdown__menu" role="listbox">
                        <button
                          type="button"
                          className={`kp-symbol-dropdown__option ${symbol === "default" ? "is-active" : ""}`}
                          onClick={() => handleSymbolSelect("default")}
                        >
                          symbol select
                        </button>
                        {selectableSymbols.length > 0 ? (
                          <>
                            <div className="kp-symbol-dropdown__divider" />
                            {selectableSymbols.map((sym) => (
                              <button
                                key={sym}
                                type="button"
                                className={`kp-symbol-dropdown__option ${sym === symbol ? "is-active" : ""}`}
                                onClick={() => handleSymbolSelect(sym)}
                              >
                                {sym}
                              </button>
                            ))}
                          </>
                        ) : (
                          <div className="kp-symbol-dropdown__empty">등록된 심볼이 없습니다</div>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="kp-symbol-controls__buttons">
                    <button onClick={handleOpenPopup} className="button_benefit_c_ut">
                      추 가
                    </button>
                    <button onClick={() => deleteSymbol(symbol)} className="button_benefit_c_ut">
                      삭 제
                    </button>
                  </div>
                </div>
                <SymbolSearchPopup
                  isOpen={isPopupOpen}
                  onAdd={handleAddSymbol}
                  onClose={handleClosePopup}
                  anchorPosition={popupAnchor}
                />
              </article>
              <AdminSharedPresetPanel
                isAdminAccess={isAdminAccess}
                featureKey="wz"
                buildPresetPayload={buildWzPresetPayload}
                applyPreset={applyWzPreset}
                reloadKey={`${uid}`}
                singleRow
              />

              <article className="kp-card kp-card--form">
            <div className="contentWrap_sp">

            <div className="line"></div>

            <div className="center_text_lever">진입 기준</div>
              <div class="leverage_container_center">
                  {/* <div class="leverage_item_c">모드 선택</div>
                  <select className="select_samaria_c" value={add_position} onChange={handleAddPosition}>
                    <option value='2'>프로</option>
                  </select> */}
                  {/* <div class="leverage_item">정리 기준</div>
                  <select className="select_samaria" value={single_double_benefit} onChange={handleSingleDoubleBenefit}>
                    <option value='single'>단독</option>
                    <option value='double'>동시</option>
                  </select> */}
              </div>
              <div class="leverage_container">
                <div class="leverage_item">진입기준</div>
                  <select className="select_samaria" value={entry_mode}
                  onChange={handleEntryMode}>
                    <option value="ut">시그널</option>
                    <option value="kinpoint">킨포인트</option>
                  </select>
                <div class="leverage_item">{candleText}</div>
                  <select className="select_samaria" value={entry_candle}
                  onChange={handleEntryCandle}>
                  <option value="5m">5 분봉</option>
                  <option value="15m">15 분봉</option>
                  <option value="30m">30 분봉</option>
                  <option value="1h">1 시간봉</option>
                  <option value="4h">4 시간봉</option>
                  <option value="1d">1 일봉</option>
                  </select>
                </div>

                <div class="leverage_container">
                  <div class="leverage_item"
                style={{
                    filter: (entry_mode !== 'ut') ? 'blur(5px)' : 'none',
                  }} disabled={entry_mode !== 'ut'} >신호 기준</div>
                <select className="select_samaria" value={candle_type_entry}
                style={{
                    filter: (entry_mode !== 'ut') ? 'blur(5px)' : 'none',
                  }}
                  disabled={entry_mode !== 'ut'}
                  onChange={handleCandleTypeEntry}>
                  <option value="haikin">하이킨아시</option>
                  <option value="normal">일반봉</option>
                </select>
                <div class="leverage_item"
                style={{
                    filter: (add_signals !== 'ut') ? 'blur(5px)' : 'none',
                  }} disabled={add_signals !== 'ut'} >진입 신호 </div>
                <select className="select_samaria" value={entry_checking}
                  onChange={handleEntryEntryChecking}>
                  <option value="-1">발생</option>
                  <option value="-2">확정</option>
                </select>
                </div>

              <div class="leverage_container">
                <div class="leverage_item" style={{
                    filter: (entry_mode !== 'ut') ? 'blur(5px)' : 'none',
                  }} disabled={entry_mode !== 'ut'}>Sensitivity</div>
                <input 
                type="number"
                class="leverage_input_basic"
                 style={{
                    filter: (entry_mode !== 'ut') ? 'blur(5px)' : 'none',
                  }} disabled={entry_mode !== 'ut'}
                value={sensitivity}
                onChange={handleSensitivity} />
                <div class="leverage_item" style={{
                    filter: (entry_mode !== 'ut') ? 'blur(5px)' : 'none',
                  }} disabled={entry_mode !== 'ut'}>ATR Period</div>
                <input 
                type="number" 
                class="leverage_input_basic"
                 style={{
                    filter: (entry_mode !== 'ut') ? 'blur(5px)' : 'none',
                  }} disabled={entry_mode !== 'ut'}
                value={atr_period}
                onChange={handleAtrperiod} />
              </div>

              <div className="line"></div>

              <div className="center_text_lever">보조 기준</div>
              <div className="leverage_container">
                <div class="leverage_item">보조 적용</div>
                <select className="select_samaria" value={entry_standard} onChange={handleEntryStandard}>
                <option value="apply">적용</option>
                <option value="no">미적용</option>
                </select>
                <div class="leverage_item" style={{
                  filter: (entry_standard !== 'apply') ? 'blur(5px)' : 'none',
                }}>EMA 기준봉갯수</div>
                <input 
                type="number"
                class="leverage_input_basic"
                value={ema_standard}
                onChange={handleEmaStandard}
                disabled={entry_standard !== 'apply'}
                style={{
                  filter: (entry_standard !== 'apply') ? 'blur(5px)' : 'none',
                }}/>
                <div class="leverage_item" style={{
                  filter: (entry_standard !== 'apply') ? 'blur(5px)' : 'none',
                }} >EMA 손절적용</div>
                <select className="select_samaria" value={ema_losscut}
                disabled={entry_standard !== 'apply'}
                style={{
                  filter: (entry_standard !== 'apply') ? 'blur(5px)' : 'none',
                }}
                onChange={handleEmaLosscut}>
                  <option value="no">미적용</option>
                  <option value="apply">적용</option>
                </select>
              </div>

              <div className="leverage_container">
                <div class="leverage_item" style={{
                  filter: (entry_standard !== 'apply' && entry_standard !== 'ema') ? 'blur(5px)' : 'none',
                }}>EMA 진입 기준간격(%)</div>
                <input
                type="number"
                min="0"
                step="0.01"
                class="leverage_input_basic"
                value={ema_entry_gap_ratio}
                onChange={handleEmaEntryGapRatio}
                disabled={entry_standard !== 'apply' && entry_standard !== 'ema'}
                style={{
                  filter: (entry_standard !== 'apply' && entry_standard !== 'ema') ? 'blur(5px)' : 'none',
                }}/>
                <div class="leverage_item" style={{
                  filter: (entry_standard !== 'apply' && entry_standard !== 'ema') ? 'blur(5px)' : 'none',
                  fontSize: '12px',
                  color: '#888',
                }}>0 = 이격 % 계산 안하고 진입 함</div>
                <div class="leverage_item" style={{
                  filter: (entry_standard !== 'apply' && entry_standard !== 'ema') ? 'blur(5px)' : 'none',
                  fontSize: '12px',
                  color: '#888',
                }}>이격이 % 초과면 진입 안 함</div>
              </div>

              <div className="leverage_container">
                <div class="leverage_item" style={{
                  filter: (entry_standard !== 'apply') ? 'blur(5px)' : 'none',
                }}>보조 시간봉</div>
                <select className="select_samaria" value={entry_standard_candle}
                disabled={entry_standard !== 'apply'}
                style={{
                  filter: (entry_standard !== 'apply') ? 'blur(5px)' : 'none',
                }}
                onChange={handleEntryStandardCandle}>
                  <option value="5m">5 분봉</option>
                  <option value="15m">15 분봉</option>
                  <option value="30m">30 분봉</option>
                  <option value="1h">1 시간봉</option>
                  <option value="4h">4 시간봉</option>
                  <option value="1d">1 일봉</option>
                </select>
                <div class="leverage_item" style={{
                  filter: (entry_standard !== 'apply') ? 'blur(5px)' : 'none',
                }} >신호 기준</div>
                <select className="select_samaria" value={entry_standard_type}
                disabled={entry_standard !== 'apply'}
                style={{
                  filter: (entry_standard !== 'apply') ? 'blur(5px)' : 'none',
                }}
                onChange={handleEntryStandardType}>
                  <option value="haikin">하이킨아시</option>
                  <option value="normal">일반봉</option>
                </select>
              </div>

              <div className="line"></div>
              
              <div className="center_text_lever">익절 기준</div>
              <div className="leverage_container">
                <div class="leverage_item" style={{
                    filter: (benefit_standard === 'plratio') ? 'blur(5px)' : 'none',
                  }} disabled={benefit_standard === 'plratio'}>정리 기준</div>
                  <select className="select_samaria" 
                    style={{
                      filter: (benefit_standard === 'plratio') ? 'blur(5px)' : 'none',
                    }} disabled={benefit_standard === 'plratio'}
                    value={single_double_benefit} onChange={handleSingleDoubleBenefit}>
                    <option value='single'>단독</option>
                    <option value='double'>동시</option>
                  </select>
              <div class="leverage_item">익절 조건</div>
                <select className="select_samaria" value={benefit_standard} onChange={handleBenefitStandard}>
                  <option value="kinpoint">킨포인트</option>
                  <option value="ut">시그널</option>
                  {/* <option value="plratio">손익비</option> */}
                </select>
                <div class="leverage_item" style={{
                    filter: (benefit_standard !== 'ut') ? 'blur(5px)' : 'none',
                  }}>발생손절</div>
                <select className="select_samaria"
                  style={{
                    filter: (benefit_standard !== 'ut') ? 'blur(5px)' : 'none',
                  }}
                  disabled={benefit_standard !== 'ut'}
                  value={occurrence_losscut}
                  onChange={handleOccurrenceLosscut}>
                  <option value='off'>미적용</option>
                  <option value='apply'>적용</option>
                </select>
              </div>
               
              <div className="leverage_container">
                  <div class="leverage_item" style={{
                    filter: (benefit_standard === 'plratio') ? 'blur(5px)' : 'none',
                  }} disabled={benefit_standard === 'plratio'}>익절 시간봉</div>
                    <select className="select_samaria" style={{
                    filter: (benefit_standard === 'plratio') ? 'blur(5px)' : 'none',
                  }} disabled={benefit_standard === 'plratio'} value={close_candle} onChange={handleCloseCandle}>
                      <option value="5m">5 분봉</option>
                      <option value="15m">15 분봉</option>
                      <option value="30m">30 분봉</option>
                      <option value="1h">1 시간봉</option>
                      <option value="4h">4 시간봉</option>
                      <option value="1d">1 일봉</option>
                    </select>
                  <div class="leverage_item"
                  style={{
                      filter: (benefit_standard !== 'ut') ? 'blur(5px)' : 'none',
                    }} disabled={benefit_standard !== 'ut'} >신호 기준</div>
                  <select className="select_samaria" value={candle_type_close}
                  style={{
                      filter: (benefit_standard !== 'ut') ? 'blur(5px)' : 'none',
                    }}
                    disabled={benefit_standard !== 'ut'}
                    onChange={handleCandleTypeClose}>
                    <option value="haikin">하이킨아시</option>
                    <option value="normal">일반봉</option>
                  </select>
                  <div class="leverage_item">익절 신호</div>
                  <select className="select_samaria" value={benefit_checking}
                    onChange={handleBenefitChecking}>
                    <option value="-1">발생</option>
                    <option value="-2">확정</option>
                  </select>
                </div>

              <div className="leverage_container">
                <div class="leverage_item" style={{
                    filter: (benefit_standard !== 'ut') ? 'blur(5px)' : 'none',
                  }} disabled={benefit_standard !== 'ut'} >Sensitivity</div>
                <input 
                type="number"
                class="leverage_input_basic"
                style={{
                    filter: (benefit_standard !== 'ut') ? 'blur(5px)' : 'none',
                  }} disabled={benefit_standard !== 'ut'} 
                value={sensitivity_close}
                onChange={handleSensitivityClose} />
                <div class="leverage_item"style={{
                    filter: (benefit_standard !== 'ut') ? 'blur(5px)' : 'none',
                  }} disabled={benefit_standard !== 'ut'} >ATR Period</div>
                <input 
                type="number" 
                class="leverage_input_basic"
                style={{
                    filter: (benefit_standard !== 'ut') ? 'blur(5px)' : 'none',
                  }} disabled={benefit_standard !== 'ut'} 
                value={atr_period_close}
                onChange={handleAtrperiodClose} />
              </div>

              {/* <div className="leverage_container">
                <div class="leverage_item" style={{
                    filter: (benefit_standard !== 'plratio') ? 'blur(5px)' : 'none',
                  }} disabled={benefit_standard !== 'plratio'} >익절비(%)</div>
                  <input 
                  type="number"
                  class="leverage_input_basic"
                  style={{
                      filter: (benefit_standard !== 'plratio') ? 'blur(5px)' : 'none',
                    }} disabled={benefit_standard !== 'plratio'} 
                  value={profit}
                  onChange={handleProfit} />
                <div class="leverage_item"style={{
                    filter: (benefit_standard !== 'plratio') ? 'blur(5px)' : 'none',
                  }} disabled={benefit_standard !== 'plratio'} >손절비(%)</div>
                  <input 
                  type="number" 
                  class="leverage_input_basic"
                  style={{
                      filter: (benefit_standard !== 'plratio') ? 'blur(5px)' : 'none',
                    }} disabled={benefit_standard !== 'plratio'} 
                  value={losscut}
                  onChange={handleLosscut} />
              </div> */}
              
              <div className="line"></div>

              <div className="leverage_container">
                <div class="leverage_item">진입 방향 선택</div>
                  <select className="select_samaria" value={direction} onChange={handleDirection}>
                    <option value='both'>양방향</option>
                    <option value='long'>롱</option>
                    <option value='short'>숏</option>
                  </select>
                <div class="leverage_item" style={{
                    filter: (mix_benefit_reentry === 'apply') ? 'blur(5px)' : 'none',
                  }}>최소 익절률(%)</div>
                <input 
                type="number" 
                class="leverage_input_basic"
                style={{
                    filter: (mix_benefit_reentry === 'apply') ? 'blur(5px)' : 'none',
                  }}
                disabled={mix_benefit_reentry === 'apply'}
                value={both_benefit_ratio}
                onChange={handleBothBenefitratio} />
              </div>

              <div className="leverage_container">
                <div class="leverage_item">절대익절(%)</div>
                  <input 
                  type="number"
                  class="leverage_input_basic"
                  value={mix_benefit}
                  onChange={handleMixBenefit} />
                <div class="leverage_item">절대익절연속진입</div>
                  <select className="select_samaria"
                  disabled={occurrence_losscut === 'apply'}
                  style={{
                    filter: (occurrence_losscut === 'apply') ? 'blur(5px)' : 'none',
                  }}
                  value={occurrence_losscut === 'apply' ? 'off' : mix_benefit_reentry}
                  onChange={handleMixBenefitReentry}>
                    <option value='off'>미적용</option>
                    <option value='apply'>적용</option>
                  </select>
              </div>

              <div className="line"></div>
              <div class="leverage_container">
                <div class="leverage_item">종합 익절 셋팅(USDT)</div>
                <input 
                type="number" 
                class="leverage_input_basic"
                value={total_benefit_usdt}
                onChange={handleTotalBenefitusdt} />
                <div class="leverage_item">종합 익절 목표값(USDT)</div>
                <input 
                type="number" 
                class="leverage_input_basic"
                value={total_benefit_usdt_obj}
                disabled = {true} />
              </div>
               <div className="button_benefit_leverage">
                <button onClick={() => onClickTotal_start(symbol)} className="button_benefit_c_lever">
                종합 익절 시작
                </button>
                <button onClick={() => onClickTotal_cancel(symbol)} className="button_benefit_c_lever">
                종합 익절 취소
                </button>
              </div>

            <div className="line"></div>

            <div className="center_text_lever">현재 진입 횟수</div>
              <div class="leverage_container">
                <div class="leverage_item" style={{ color: '#f8fafc' }}>{leverageText}</div>
                <input 
                type="number"
                class="leverage_input_basic"
                value={long_count}
                disabled={true} />
                <div class="leverage_item" style={{ color: '#f8fafc' }}>{leverageText_short}</div>
                {!(add_position === '1' || add_position === 1) && (
                <input 
                type="number" 
                className="leverage_input_basic"
                value={short_count}
                disabled={true}/>)}
              </div>
            {/* 레버리지 셋팅  레버리지 셋팅 레버리지 셋팅 레버리지 셋팅 레버리지 셋팅*/}
            <div className="line"></div>
            <div className="leverage_container">
              <div class="leverage_item">최초진입 USDT</div>
                <input 
                type="number"
                class="leverage_input_basic"
                value={first_usdt}
                onChange={handleFirstUsdt} />
                <div class="leverage_item">Set Leverage</div>
                <input 
                type="number" 
                class="leverage_input_basic"
                value={leverage_info}
                onChange={handleLeverage} />  
            </div>
            <div className="line"></div>
            <div className="wz-entry-table">
              <div className="wz-entry-table__column">
                <div className="wz-entry-table__title red-text">{setText_long}</div>
                <div className="leverage_container_center_kp wz-entry-table__header">
                  <div className="leverage_item_kp">순 서</div>
                  <div className="leverage_item_kp">USDT</div>
                  <div className="leverage_item_kp">간격(%)</div>
                </div>
                {entryRows.map((row) => {
                  const isLongUsdtDisabled = add_position === '0' || add_position === 0;
                  const isLongRatioDisabled = add_position === '0' || add_position === 0 || add_position === '1' || add_position === 1;
                  return (
                    <div className="leverage_container_center_kp" key={`long-${row.label}`}>
                      <div className="leverage_item_kp">{row.label}</div>
                      <input
                        type="number"
                        step="1"
                        className="leverage_input_kp"
                        value={row.longUsdt}
                        disabled={isLongUsdtDisabled}
                        style={{
                          filter: isLongUsdtDisabled ? 'blur(5px)' : 'none',
                        }}
                        onChange={row.onLongUsdtChange}
                      />
                      <input
                        type="number"
                        className="leverage_input_kp"
                        value={row.longRatio}
                        disabled={isLongRatioDisabled}
                        style={{
                          filter: isLongRatioDisabled ? 'blur(5px)' : 'none',
                        }}
                        onChange={row.onLongRatioChange}
                      />
                    </div>
                  );
                })}
              </div>

              <div className="wz-entry-table__column">
                <div className="wz-entry-table__title blue-text">{setText_short}</div>
                <div className="leverage_container_center_kp wz-entry-table__header">
                  <div className="leverage_item_kp">순 서</div>
                  <div className="leverage_item_kp">USDT</div>
                  <div className="leverage_item_kp">간격(%)</div>
                </div>
                {entryRows.map((row) => {
                  const isShortDisabled = add_position === '1' || add_position === 1;
                  return (
                    <div className="leverage_container_center_kp" key={`short-${row.label}`}>
                      <div className="leverage_item_kp">{row.label}</div>
                      <input
                        type="number"
                        step="1"
                        className="leverage_input_kp"
                        value={row.shortUsdt}
                        disabled={isShortDisabled}
                        style={{
                          filter: isShortDisabled ? 'blur(5px)' : 'none',
                        }}
                        onChange={row.onShortUsdtChange}
                      />
                      <input
                        type="number"
                        className="leverage_input_kp"
                        value={row.shortRatio}
                        disabled={isShortDisabled}
                        style={{
                          filter: isShortDisabled ? 'blur(5px)' : 'none',
                        }}
                        onChange={row.onShortRatioChange}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="line"></div>
            
            </div>

            <OpenPositionsSnapshotSection
              endpoint="/wz_open_positions_snapshot"
              enabled={Boolean(uid)}
              reloadKey={`${uid}-${snapshotReloadToken}`}
              fetchImpl={fetchWithCredentials}
              title="메인/보조 오픈 포지션"
              updatedAtPrefix="마지막 갱신:"
              loadingRefreshLabel="조회 중..."
              onSelectSymbol={onClickCoin}
              selectedSymbol={symbol}
              showAdminManualTrade={showAdminManualTrade}
              onAdminNewEntry={
                showAdminManualTrade
                  ? (e) =>
                      setAdminManualModal({
                        mode: "new",
                        anchorRect: snapshotAnchorRect(e.currentTarget),
                      })
                  : undefined
              }
              onAdminAdjustLeg={
                showAdminManualTrade
                  ? (payload) =>
                      setAdminManualModal({
                        mode: "leg",
                        account: payload.account,
                        item: payload.item,
                        anchorRect: payload.anchorRect ?? null,
                      })
                  : undefined
              }
            />
            {showAdminManualTrade && adminManualModal ? (
              <HoldAdminManualModal
                open
                payload={adminManualModal}
                onClose={() => setAdminManualModal(null)}
                onOrderSuccess={() => setSnapshotReloadToken((t) => t + 1)}
                fetchHold={fetchWithCredentials}
                manualOrderEndpoint="/wz/admin_manual_order"
                hasSecondAccount={memberHasSecondBitget}
                pageSymbol={symbol}
                selectableSymbols={selectableSymbols}
                holdTargetUid={String(uid || "")}
                isAdminAccess={isAdminAccess}
                pageLeverage={leverage_info}
              />
            ) : null}
            <div className="line"></div>

            <div className="button_benefit_leverage">
                <button onClick={() => onClickStart()} className="bottomButton_sp_lever">
                매매 시작
                </button>
            </div>

            <div className="button_benefit_leverage">
                <button onClick={() => onClickBenefit_stop(symbol)} disabled={benefit_stop === 1 || trade_start === 0 || benefit_stop === '1' || trade_start === '0'} className="button_benefit_c_lever">
                익절 후 정리
                </button>
                <button onClick={() => onClickBenefit_cancel(symbol)} disabled={benefit_stop === '0' || benefit_stop === 0} className="button_benefit_c_lever">
                취 소
                </button>
            </div>

            <div className="button_benefit_leverage">
                <button onClick={() => onClickCurrentCoin_stop(symbol)} disabled={(symbol === 'default' && trade_start === 0 &&  benefit_stop === 0) || (symbol === 'default' && trade_start === '0' &&  benefit_stop === '0') || setlevel <= 7} className="bottomButton_sp_lever">
                현재 코인 정리
                </button>
                {/* <button onClick={() => onClickAllCoin_stop()} disabled={symbol === 'default' && setlevel <= 7} className="button_benefit_c_lever">
                All symbol close
                </button> */}
            </div>
            <div className="button_benefit_leverage">
                <button onClick={() => onClickCurrentCoin_long_stop(symbol)} disabled={symbol === 'default'} className="button_benefit_c_lever">
                LONG강제정리
                </button>
                <button onClick={() => onClickCurrentCoin_short_stop(symbol)} disabled={symbol === 'default'} className="button_benefit_c_lever">
                SHORT강제정리
                </button>
            </div>

            <div className="button_benefit_leverage">
                <button onClick={() => onClickEnd()} disabled={notAllow} className="bottomButton_sp_lever">
                로그아웃
                </button>
             </div>
          </article>
            </div>

            <aside className="kp-console__side">
              <article className="kp-card kp-card--list">
                <div className="list_box_title_lever">진입중인 코인 리스트</div>
                <ul className="list_box_lever">
                  {e_items.map((item, index) => (
                    <li
                      key={index}
                      className="list_item"
                      onClick={() => onClickCoin(item)}
                    >
                      {item}
                    </li>
                  ))}
                </ul>
              </article>

              <article className="kp-card kp-card--list">
                <div className="list_box_title_lever">익절 후 정리 리스트</div>
                <ul className="list_box_lever">
                  {w_items.map((item, index) => (
                    <li
                      key={index}
                      className="list_item"
                      onClick={() => onClickCoin(item)}
                    >
                      {item}
                    </li>
                  ))}
                </ul>
              </article>
            </aside>
          </div>
        </section>
      </main>
    );
}